/* eslint-disable no-empty */

import { useState, useEffect, useRef, useMemo } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import useTheme from './useTheme.js';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import {
  Plus,
  Minus,
  Compass,
  Navigation,
  Phone,
  Mail,
  ShieldCheck,
  Building2,
  BatteryCharging,
  Maximize2,
  Minimize2,
  X,
  Users,
  Sparkles,
  Layers,
  CircleDot,
  HelpCircle,
  Play,
  Pause,
  Circle,
  Check,
  Radar,
} from 'lucide-react';
import Card3D from './Card3D.js';
import { toast } from 'sonner';
import {
  PH_CITY_COORDS_MAPVIEW as PH_CITY_COORDS,
  ymdLocal,
  tryFieldAny,
  extractTimestampMillis,
  isLoginTypeRecord,
  isLogoutTypeRecord,
  extractLoginsPerDay,
  pickLocationCity,
  hasTodayLoginInBucket,
} from '../../utils/mapViewPipelineUtils';

/* ------------------------------------------------------------------ */
/*  INLINE MOCK DATA (formerly mapview/src/data/mockData.ts)           */
/* ------------------------------------------------------------------ */
const INITIAL_EMPLOYEES = [];
const INITIAL_CLIENTS = [];


const MAP_LAYERS = [
  { id: 'voyager', name: 'Carto Voyager (Clean & Modern)', url: 'https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png', attribution: '&copy; OpenStreetMap &copy; CARTO', subdomains: ['a', 'b', 'c', 'd'], maxZoom: 19 },
  { id: 'osm', name: 'OpenStreetMap (Standard Free)', url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', attribution: '&copy; OpenStreetMap contributors', maxZoom: 19 },
  { id: 'positron', name: 'Carto Positron (High Contrast Light)', url: 'https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png', attribution: '&copy; OpenStreetMap &copy; CARTO', subdomains: ['a', 'b', 'c', 'd'], maxZoom: 19 },
  { id: 'dark', name: 'Carto Dark Matter (Night Navy)', url: 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png', attribution: '&copy; OpenStreetMap &copy; CARTO', subdomains: ['a', 'b', 'c', 'd'], maxZoom: 19 },
];

const EMP_STATUSES = ['active', 'in_transit', 'on_site', 'available', 'off_duty'];
const CLIENT_TIERS = ['Enterprise', 'VIP', 'Commercial', 'Prospect'];
const DEPARTMENTS = ['Field Engineering', 'Account Management', 'Technical Support', 'Operations', 'Logistics', 'General Staff', 'Human Resources', 'Sales & Marketing', 'Executive Office'];
const INDUSTRIES = ['Technology', 'Healthcare', 'Renewable Energy', 'Logistics & Supply', 'Finance', 'Manufacturing', 'MapPoint Line', 'Government', 'Retail', 'Education'];

/* ------------------------------------------------------------------ */
/*  INLINE MARKER ICON BUILDERS (no Tailwind classNames needed — raw)  */
/* ------------------------------------------------------------------ */

function createEmployeeIcon(emp, isSelected) {
  const isMoving = emp.status === 'in_transit';
  const isOnSite = emp.status === 'on_site';
  let statusColor = '#22c55e';
  if (isMoving) statusColor = '#38bdf8';
  if (isOnSite) statusColor = '#facc15';
  if (emp.status === 'off_duty') statusColor = '#94a3b8';
  const pulse = (emp.status === 'active' || isMoving)
    ? `<div style="position:absolute;inset:-8px;border-radius:9999px;background:rgba(59,130,246,0.35);opacity:0.45;animation:mapview-ping-slow 2.2s cubic-bezier(0,0,0.2,1) infinite;pointer-events:none;"></div>`
    : '';
  const selRing = isSelected
    ? `box-shadow:0 0 0 4px rgba(250,204,21,0.85);transform:scale(1.1);`
    : '';
  const html = `
    <div style="position:relative;cursor:pointer;${selRing}" class="nad-mapview-marker-employee">
      ${pulse}
      <div style="position:relative;display:flex;flex-direction:column;align-items:center;">
        <div style="width:40px;height:40px;border-radius:16px;background:linear-gradient(135deg,#1d4ed8 0%,#2563eb 45%,#4f46e5 100%);padding:2px;box-shadow:0 10px 25px -8px rgba(30,58,138,0.7);border:2px solid rgba(250,204,21,0.9);display:flex;align-items:center;justify-content:center;">
          <img src="${emp.avatar}" alt="${emp.name}" style="width:100%;height:100%;object-fit:cover;border-radius:14px;" />
          <span style="position:absolute;top:-2px;right:-2px;width:14px;height:14px;border-radius:9999px;border:2px solid #0f172a;background-color:${statusColor};"></span>
          <div style="position:absolute;bottom:-2px;left:-2px;background:rgba(250,204,21,1);color:#020617;font-weight:900;font-size:9px;padding:1px 4px;border-radius:4px;box-shadow:0 1px 2px rgba(0,0,0,0.3);border:1px solid #0f172a;line-height:1.3;">EMP</div>
        </div>
        <div style="width:10px;height:10px;background:#1d4ed8;transform:rotate(45deg);margin-top:-4px;border-right:2px solid rgba(250,204,21,0.8);border-bottom:2px solid rgba(250,204,21,0.8);box-shadow:0 3px 5px rgba(0,0,0,0.25);"></div>
      </div>
    </div>`;
  return L.divIcon({
    html,
    className: 'nad-mapview-div-icon',
    iconSize: [40, 52],
    iconAnchor: [20, 50],
    popupAnchor: [0, -46],
  });
}

function createClientIcon(client, isSelected) {
  const tierLabel = client.tier === 'Enterprise' ? 'ENT' : client.tier === 'VIP' ? 'VIP' : 'CLI';
  const pulse = client.tier === 'Enterprise'
    ? `<div style="position:absolute;inset:-8px;border-radius:9999px;background:rgba(250,204,21,0.35);opacity:0.45;animation:mapview-ping-slow 2.2s cubic-bezier(0,0,0.2,1) infinite;pointer-events:none;"></div>`
    : '';
  const selRing = isSelected
    ? `box-shadow:0 0 0 4px rgba(37,99,235,0.85);transform:scale(1.1);`
    : '';
  const svgBuilding = `<svg width="20" height="20" viewBox="0 0 24 24" fill="#020617"><path d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"/></svg>`;
  const html = `
    <div style="position:relative;cursor:pointer;${selRing}" class="nad-mapview-marker-client">
      ${pulse}
      <div style="position:relative;display:flex;flex-direction:column;align-items:center;">
        <div style="width:40px;height:40px;border-radius:16px;background:linear-gradient(135deg,#eab308 0%,#fbbf24 45%,#fde047 100%);padding:4px;box-shadow:0 10px 25px -8px rgba(120,53,15,0.6);border:2px solid #0c1a3a;display:flex;flex-direction:column;align-items:center;justify-content:center;color:#020617;">
          ${svgBuilding}
          <div style="position:absolute;bottom:-2px;right:-2px;background:#1e3a8a;color:#facc15;font-weight:900;font-size:8px;padding:1px 3px;border-radius:4px;box-shadow:0 1px 2px rgba(0,0,0,0.3);border:1px solid rgba(250,204,21,1);line-height:1.3;">${tierLabel}</div>
        </div>
        <div style="width:10px;height:10px;background:#f59e0b;transform:rotate(45deg);margin-top:-4px;border-right:2px solid #0c1a3a;border-bottom:2px solid #0c1a3a;box-shadow:0 3px 5px rgba(0,0,0,0.25);"></div>
      </div>
    </div>`;
  return L.divIcon({
    html,
    className: 'nad-mapview-div-icon',
    iconSize: [40, 52],
    iconAnchor: [20, 50],
    popupAnchor: [0, -46],
  });
}

/* ------------------------------------------------------------------ */
/*  MAP VIEW PAGE                                                     */
/* ------------------------------------------------------------------ */

function MapIntroCurtain({ show, isLight }) {
  const accent = isLight ? '#2563eb' : '#facc15';
  const accentSoft = isLight ? 'rgba(37,99,235,0.78)' : 'rgba(250,204,21,0.82)';
  const pageBg = isLight ? '#ffffff' : '#0f172a';
  const pageBgGradient = isLight
    ? 'radial-gradient(1200px 800px at 12% -10%, rgba(219,234,254,0.95) 0%, rgba(255,255,255,0) 55%), radial-gradient(1100px 700px at 100% 120%, rgba(254,249,195,0.9) 0%, rgba(255,255,255,0) 55%), linear-gradient(135deg,#eff6ff 0%, #ffffff 45%, #fefce8 100%)'
    : undefined;
  const titleClass = isLight
    ? 'mt-6 font-display text-3xl font-semibold tracking-tight text-white'
    : 'mt-6 font-display text-3xl font-semibold tracking-tight text-white';
  const captionClass = isLight
    ? 'mt-3 font-display text-xs text-slate-500 italic'
    : 'mt-3 font-display text-xs italic';
  const dividerBg = isLight ? 'rgba(15,23,42,0.12)' : 'rgba(255,255,255,0.08)';
  const tileCls = isLight
    ? 'relative grid size-20 place-items-center rounded-[28px] p-2.5 border bg-white'
    : 'relative grid size-20 place-items-center rounded-[28px] bg-white p-2.5 border border-white/10';
  const tileStyle = isLight
    ? {
        borderColor: 'rgba(15,23,42,0.08)',
        boxShadow: '0 2px 4px rgba(15,23,42,0.08), 0 24px 48px -20px rgba(15,23,42,0.22)',
      }
    : {
        boxShadow:
          '0 24px 60px -18px rgba(250,204,21,0.35), 0 0 0 1px rgba(250,204,21,0.15) inset',
      };
  return (
    <AnimatePresence>
      {show && (
        <motion.div
          key="map-intro-curtain"
          initial={{ opacity: 1 }}
          exit={{ opacity: 0, scale: 1.06, filter: 'blur(10px)' }}
          transition={{ duration: 0.75, ease: [0.65, 0, 0.35, 1] }}
          className="fixed inset-0 z-[140] grid place-items-center"
          style={{
            background: pageBgGradient || pageBg,
            backgroundColor: pageBg,
          }}
        >
          <div className="flex flex-col items-center">
            <motion.span
              initial={{ opacity: 0, scale: 0.6, rotate: -22 }}
              animate={{ opacity: 1, scale: 1, rotate: 0 }}
              transition={{ type: 'spring', stiffness: 150, damping: 13, delay: 0.05 }}
              className={tileCls}
              style={tileStyle}
            >
              <motion.span
                className="absolute inset-0 rounded-[28px] border-2"
                style={{ borderColor: accent }}
                initial={{ opacity: 0.9, scale: 1 }}
                animate={{ opacity: 0, scale: 1.55 }}
                transition={{ duration: 1.5, repeat: Infinity, ease: 'easeOut' }}
              />
              <img src="/iSource LOGO1.png" alt="iSource" className="w-full h-full object-contain" />
            </motion.span>

            <motion.h1
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
              className={titleClass}
              style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif" }}
            >
              iSource Map View
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.42, ease: [0.22, 1, 0.36, 1] }}
              className="mt-2 text-[10px] font-bold tracking-[0.34em] uppercase"
              style={{ color: accentSoft }}
            >
              Field Operations Radar
            </motion.p>

            <div
              className="relative mt-8 h-px w-56 overflow-hidden"
              style={{ background: dividerBg }}
            >
              <motion.span
                className="absolute inset-y-0 left-0"
                style={{ background: accent }}
                initial={{ width: '0%' }}
                animate={{ width: '100%' }}
                transition={{ duration: 1.2, delay: 0.2, ease: [0.4, 0, 0.2, 1] }}
              />
            </div>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6 }}
              className={captionClass}
              style={{ color: isLight ? 'rgba(15,23,42,0.55)' : accentSoft }}
            >
              Locating teams and routes…
            </motion.p>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default function MapViewPage({ axiosInstance }) {
  const { isLight } = useTheme();
  const [minElapsed, setMinElapsed] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setMinElapsed(true), 1400);
    return () => clearTimeout(t);
  }, []);
  const [employees, setEmployees] = useState(INITIAL_EMPLOYEES);
  const [clients, setClients] = useState(INITIAL_CLIENTS);
  const [filterOptions, setFilterOptions] = useState({
    showEmployees: true,
    showClients: true,
    employeeStatuses: [...EMP_STATUSES],
    clientTiers: [...CLIENT_TIERS],
    departments: [],
    industries: [],
    searchQuery: '',
    hiddenEmployeeIds: [],
    hiddenClientIds: [],
  });
  const [selectedEntity, setSelectedEntity] = useState(null);
  const [radiusOrigin, setRadiusOrigin] = useState(null);
  const [radiusKm, setRadiusKm] = useState(15);
  const [activeTileLayerId, setActiveTileLayerId] = useState('osm');
  const [isSimulating, setIsSimulating] = useState(false);
  const [isRadiusModalOpen, setIsRadiusModalOpen] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isHelpModalOpen, setIsHelpModalOpen] = useState(false);
  const [clickedCoords, setClickedCoords] = useState(null);
  const [filterOpen, setFilterOpen] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [savedLines, setSavedLines] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [reloadTick, setReloadTick] = useState(0);

  const [tileFailCount, setTileFailCount] = useState(0);
  const lastTileErrorsRef = useRef([]);
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const tileLayerRef = useRef(null);
  const markersLayerRef = useRef(null);
  const routeLayerRef = useRef(null);
  const radiusLayerRef = useRef(null);
  const linesLayerRef = useRef(null);

  /* --------- INJECT SCOPED STYLES ONCE --------- */
  useEffect(() => {
    const style = document.createElement('style');
    style.setAttribute('data-mapview-styles', 'true');
    style.textContent = `
      @keyframes mapview-ping-slow {
        0% { transform: scale(0.95); opacity: 0.8; }
        70% { transform: scale(1.8); opacity: 0; }
        100% { transform: scale(1.8); opacity: 0; }
      }
      .nad-mapview-page { position: relative; height: 100%; width: 100%; }
      /*
        Card3D normally sets translateZ(28px) + preserve-3d on its inner content
        wrapper. That 3D context interleaves with Leaflet's internal translate3d
        panes so hit-tests on our absolute overlays get routed to Leaflet instead.
        For THIS page we flatten the 3D context so our buttons truly paint on top.
      */
      .nad-mapview-page .card-3d-content {
        transform: none !important;
        transform-style: flat !important;
        position: relative;
        z-index: 1;
      }
      .nad-mapview-page .leaflet-container {
        font-family: 'Plus Jakarta Sans', system-ui, -apple-system, sans-serif !important;
        width: 100%; height: 100%; z-index: 1; background: #0f172a; border-radius: 20px;
      }
      /*
        Leaflet creates .leaflet-control-container ALWAYS (even with zero
        controls) and its top/bottom/left/right anchor divs cover the full map
        viewport with their own z-index 1000 stack. Disable pointer events on
        the whole control container — then opt back in ONLY for the attribution
        link, so attribution remains clickable but our overlay buttons are the
        exclusive hit targets everywhere else.
      */
      .nad-mapview-page .leaflet-control-container,
      .nad-mapview-page .leaflet-control-container * {
        pointer-events: none !important;
      }
      .nad-mapview-page .leaflet-control-attribution,
      .nad-mapview-page .leaflet-control-attribution * {
        pointer-events: auto !important;
      }
      .nad-mapview-page .leaflet-popup-content-wrapper {
        padding: 0; border-radius: 16px;
        box-shadow: 0 20px 25px -5px rgba(15,23,42,0.4), 0 8px 10px -6px rgba(15,23,42,0.3);
        overflow: hidden; border: 1px solid rgba(234,179,8,0.3);
        background: #0f172a; color: #f8fafc;
      }
      .nad-mapview-page .leaflet-popup-content { margin: 0 !important; line-height: 1.4; width: 300px !important; }
      .nad-mapview-page .leaflet-popup-tip { background: #0f172a !important; border: 1px solid rgba(234,179,8,0.3); }
      .nad-mapview-page .leaflet-popup-close-button {
        top: 10px !important; right: 10px !important; color: #94a3b8 !important;
        padding: 4px !important; width: 24px !important; height: 24px !important;
        border-radius: 50% !important; background: rgba(15,23,42,0.6) !important;
        backdrop-filter: blur(4px); display: flex !important;
        align-items: center !important; justify-content: center !important;
        transition: all 0.2s ease !important;
      }
      .nad-mapview-page .leaflet-popup-close-button:hover {
        color: #facc15 !important; background: rgba(30,58,138,0.8) !important;
      }
      .nad-mapview-marker-employee, .nad-mapview-marker-client {
        transition: transform 0.25s cubic-bezier(0.34,1.56,0.64,1);
      }
      .nad-mapview-marker-employee:hover, .nad-mapview-marker-client:hover {
        transform: scale(1.18) translateY(-4px); z-index: 1000 !important;
      }
      .nad-mapview-div-icon { background: transparent !important; border: none !important; }
      .nad-mapview-page ::-webkit-scrollbar { width: 6px; height: 6px; }
      .nad-mapview-page ::-webkit-scrollbar-track { background: rgba(15,23,42,0.6); }
      .nad-mapview-page ::-webkit-scrollbar-thumb { background: #1e3a8a; border-radius: 9999px; }
      .nad-mapview-page ::-webkit-scrollbar-thumb:hover { background: #eab308; }
      /*
        Every overlay group that should receive clicks carries this class.
        Force pointer-events:auto on the whole subtree so nothing upstream
        (Leaflet, Card3D glare, intro-curtain) can ever disable them.
      */
      .nad-mapview-ui-control,
      .nad-mapview-ui-control * {
        pointer-events: auto !important;
      }
    `;
    document.head.appendChild(style);
    return () => { document.head.removeChild(style); };
  }, []);

  /* --------- GPS SIMULATION --------- */
  useEffect(() => {
    if (!isSimulating) return undefined;
    const interval = setInterval(() => {
      setEmployees((prev) =>
        prev.map((emp) => {
          if (emp.status === 'in_transit' || emp.status === 'active') {
            const deltaLat = (Math.random() - 0.48) * 0.003;
            const deltaLng = (Math.random() - 0.48) * 0.003;
            return {
              ...emp,
              lat: emp.lat + deltaLat,
              lng: emp.lng + deltaLng,
              lastActive: 'Just now',
            };
          }
          return emp;
        })
      );
    }, 2000);
    return () => clearInterval(interval);
  }, [isSimulating]);

  /* --------- FETCH LIVE DATA: MapPoint lines → Clients, Employees + Attendance last_logout → Employees --------- */
  useEffect(() => {
    let mounted = true;
    if (!axiosInstance) return undefined;
    setIsLoading(true);
    setLoadError(null);

    const shapeLinesAsClients = (lines) => {
      const clients = [];
      (Array.isArray(lines) ? lines : []).forEach((line) => {
        const points = Array.isArray(line.points) ? line.points : [];
        if (points.length === 0) return;
        const first = points[0];
        const last = points[points.length - 1];
        if (!first || first.length < 2 || typeof first[0] !== 'number' || typeof first[1] !== 'number') return;
        const color = (line.color || '#ef4444').toLowerCase();
        let tier = 'Prospect';
        if (color.startsWith('#ef44') || color.startsWith('#ef4444')) tier = 'VIP';
        else if (color.startsWith('#facc15') || color.startsWith('#facc') || color.startsWith('#eab308')) tier = 'Enterprise';
        else if (color.startsWith('#22c55e') || color.startsWith('#22c5')) tier = 'Commercial';
        else if (color.startsWith('#3b82f6') || color.startsWith('#3b82')) tier = 'Enterprise';
        const tag = (line.tag || '').trim();
        const company = tag || `Saved Route ${String(line.id || '').slice(0, 6)}`;
        let address = '';
        let city = '';
        if (points.length >= 2 && Array.isArray(last) && last.length >= 2) {
          address = `Route: ${points.length} waypoints • ends at (${last[0].toFixed(3)}, ${last[1].toFixed(3)})`;
          city = `Polyline (${points.length})`;
        } else {
          city = `Waypoint`;
          address = `Single waypoint at (${first[0].toFixed(3)}, ${first[1].toFixed(3)})`;
        }
        clients.push({
          id: `mp-line-${line.id || String(Math.random()).slice(2, 10)}`,
          company,
          contactPerson: tag ? 'Line Contact' : 'Saved Route Contact',
          role: 'MapPoint Line',
          email: '',
          phone: '',
          address,
          city,
          industry: 'MapPoint Line',
          tier,
          lat: first[0],
          lng: first[1],
          contractValue: '',
          activeProjects: points.length,
          assignedEmployeeIds: [],
          notes: line.color ? `Line color: ${line.color}${line.weight ? `, weight: ${line.weight}` : ''}` : '',
          _source: 'mappoint',
          _lineId: line.id || null,
          _visible: line.visible !== false,
          _color: line.color || '#ef4444',
          _weight: line.weight || 4,
          _points: points,
        });
      });
      return clients;
    };

    const buildEmployeesFromAttendance = async () => {
      const empRes = await axiosInstance
        .get('/employees')
        .catch((err) => {
          // eslint-disable-next-line no-console
          console.warn('[MapView][FETCH] GET /employees error:', err?.message || err);
          return { data: [] };
        });
      const empList = Array.isArray(empRes?.data) ? empRes.data : [];

      const todayKeyLocal = ymdLocal(new Date());
      const isTodayLocal = (r) => {
        const ms = extractTimestampMillis(r);
        if (!ms) return false;
        return ymdLocal(new Date(ms)) === todayKeyLocal;
      };

      const fetchAttendanceEndpoint = async (label, url, config = {}) => {
        const t0 = Date.now();
        try {
          const data = await axiosInstance.get(url, config);
          const arr = Array.isArray(data.data) ? data.data : [];
          // eslint-disable-next-line no-console
          console.info(`[MapView][FETCH] ${label} -> ${arr.length} records (${Date.now() - t0}ms)`, {
            url,
            sampleKeys: arr[0] ? Object.keys(arr[0]).slice(0, 25) : null,
            sampleFirst: arr[0] ? (() => {
              const { password, photo, selfie_photo, premises_photo, image, ...rest } = arr[0];
              return rest;
            })() : null,
          });
          return arr;
        } catch (axiosErr) {
          // eslint-disable-next-line no-console
          console.warn('[MapView][FETCH] endpoint HTTP error:', { url, status: axiosErr?.response?.status, msg: axiosErr?.message });
          return [];
        }
      };

      const endpointResults = await Promise.allSettled([
        fetchAttendanceEndpoint(
          'employee_attendance/today (user-scoped endpoint — returns only calling-user own records, so 0 for admin is expected)',
          '/employee_attendance/today'
        ),
        fetchAttendanceEndpoint(
          'employee_attendance/all (admin bulk, limit=5000 max, dedupe later client-side)',
          '/employee_attendance/all',
          { params: { limit: 5000, include_photos: false, no_user_scope: true, all: true } }
        ),
        fetchAttendanceEndpoint(
          'attendance/today (legacy user-scoped today endpoint)',
          '/attendance/today'
        ),
        fetchAttendanceEndpoint(
          'attendance (legacy bulk endpoint, include_photos=false, limit=5000)',
          '/attendance',
          { params: { include_photos: false, limit: 5000 } }
        ),
        fetchAttendanceEndpoint(
          'attendance/recent (last 48h fallback endpoint, include_photos=false)',
          '/attendance',
          { params: { include_photos: false, recent_hours: 48, limit: 5000 } }
        ),
      ]);

      const rawMerge = [];
      endpointResults.forEach((result, idx) => {
        if (result.status === 'fulfilled') {
          if (Array.isArray(result.value)) {
            rawMerge.push(...result.value);
            // eslint-disable-next-line no-console
            console.info(`[MapView][FETCH] endpoint ${idx} fulfilled -> ${result.value.length} records`);
          }
        } else {
          // eslint-disable-next-line no-console
          console.warn('[MapView][FETCH] endpoint permanently failed:', {
            reason: result.reason?.message || String(result.reason),
            stackHead: (result.reason?.stack || '').slice(0, 200),
          });
        }
      });

      const todayFiltered = rawMerge.filter(isTodayLocal);
      const attPrefilter = todayFiltered.length > 0 ? todayFiltered : rawMerge;

      const seenAtt = new Set();
      const attList = [];
      attPrefilter.forEach((r) => {
        if (!r) return;
        const key = JSON.stringify([
          r.user_id || r.employee_id || r.employeeId || r.emp_id || (r._id ? String(r._id) : null) || (r.id ? String(r.id) : null),
          r.type || r.action || '',
          r.timestamp || r.created_at || r.time_in || 0,
          r.latitude ?? r.longitude ?? r.location ?? r.city_name ?? ''
        ]);
        if (seenAtt.has(key)) return;
        seenAtt.add(key);
        attList.push(r);
      });

      if (!attList.length) {
        // eslint-disable-next-line no-console
        console.warn('[MapView][FETCH] 0 attendance records after merge + dedupe. Will attempt /employees directory-only pin rendering (pipeline pre-step dirOnlyPins).', {
          rawMergeCount: rawMerge.length,
          endpointCount: endpointResults.length,
          employeesDirCount: empList.length,
        });
      } else {
        // eslint-disable-next-line no-console
        console.info('[MapView][FETCH] attendance merge summary:', {
          rawTotal: rawMerge.length,
          todayOnlyCount: todayFiltered.length,
          dedupedFinal: attList.length,
        });
      }

      const byUser = new Map();
      const addToBucket = (key, rec) => {
        if (!key || key === '') return;
        const k = String(key);
        const bucket = byUser.get(k) || [];
        bucket.push(rec);
        byUser.set(k, bucket);
      };
      attList.forEach((rec) => {
        if (!rec) return;
        addToBucket(rec.user_id, rec);
        addToBucket(rec.employee_id, rec);
        addToBucket(rec.employeeId, rec);
        addToBucket(rec.emp_id, rec);
        addToBucket(rec.userId, rec);
        // NOTE: intentionally NOT bucketing by rec.id / rec._id — those are
        // per-record identifiers, not employee identifiers. Bucketing by them
        // created one phantom "employee" per attendance row, which duplicated
        // pins for anyone with multiple time-ins in a day.
      });

      // ----- PRE-PASS 0 (RESTORED per user direction): directory-employee CANDIDATE scan
      // with strict TODAY-ONLY ATTENDANCE FILTER.
      //
      // User's rule (verbatim):
      //   "revert it back to the earlier adjustments where all employees are visible,
      //    then filter the data to show only the pin of the employee with its recent
      //    login on the present day only. if the employee has a recent login yesterday
      //    or older, hide it on the map."
      //
      // Semantics:
      //  - Iterate EVERY directory employee (empList) → ALL employees are candidates
      //    (revert to "all employees visible" candidate universe).
      //  - BUT before pinning, CROSS-REFERENCE each employee to their attendance
      //    bucket (by 7 ID forms + display_name) and demand that the bucket contains
      //    at least one login-type record with a timestamp equal to TODAY's local
      //    calendar date.
      //  - If today's attendance login EXISTS → push a pin via pushRecord (pushRecord
      //    itself still enforces strict today anchor for coords and relative time,
      //    so SelectedCard shows the correct first-login-today info).
      //  - If today's attendance login is ABSENT (yesterday login / older / never) →
      //    skip the pin silently (employee "yesterday or older, hide it on the map").
      const todayKeyGlobal = ymdLocal(new Date());
      const todayKeyForPrePass = todayKeyGlobal;
      const findBucketByEmployee = (e) => {
        const ids = new Set();
        const addId = (x) => { if (x != null && x !== '') ids.add(String(x)); };
        addId(e.id); addId(e.employee_id); addId(e.user_id); addId(e.employeeId);
        addId(e.emp_id); addId(e._id ? String(e._id) : null); addId(e.id ? String(e.id) : null);
        addId(e.userId);
        const nameNeedle =
          (typeof (e.name || e.full_name || `${e.first_name || ''} ${e.last_name || ''}`.trim() || e.display_name || '') === 'string' &&
            (e.name || e.full_name || `${e.first_name || ''} ${e.last_name || ''}`.trim() || e.display_name || '').trim().toLowerCase()) || '';
        let best = [];
        for (const [bucketUid, bucketRecs] of byUser.entries()) {
          if (ids.has(String(bucketUid))) { best = bucketRecs; break; }
          const matchId = (bucketRecs || []).some((r) => {
            const bids = [
              r.user_id, r.employee_id, r.employeeId, r.emp_id, r.userId,
              r._id ? String(r._id) : null, r.id ? String(r.id) : null,
            ].filter((x) => x != null && x !== '').map((x) => String(x));
            return bids.some((b) => ids.has(b));
          });
          if (matchId) { best = bucketRecs; break; }
          if (nameNeedle && (bucketRecs || []).length) {
            const rep = bucketRecs[0];
            const hay = (
              (typeof rep?.full_name === 'string' ? rep.full_name : '') +
              ' ' + (typeof rep?.user_name === 'string' ? rep.user_name : '') +
              ' ' + (typeof rep?.name === 'string' ? rep.name : '') +
              ' ' + (typeof rep?.display_name === 'string' ? rep.display_name : '')
            ).toLowerCase();
            if (hay && (hay.includes(nameNeedle) || nameNeedle.includes(hay.trim()))) {
              best = bucketRecs;
              break;
            }
          }
        }
        return best;
      };
      const dirOnlyPins = [];
      (empList || []).forEach((e) => {
        try {
          const bucket = findBucketByEmployee(e);
          if (!hasTodayLoginInBucket(bucket, todayKeyForPrePass)) return; // hide yesterday or older
          const id =
            e.id ||
            e.employee_id ||
            e.user_id ||
            e.employeeId ||
            e.emp_id ||
            (e._id ? String(e._id) : null);
          const preferredName =
            e.name || e.full_name || `${e.first_name || ''} ${e.last_name || ''}`.trim() || e.display_name || '';
          const preferredDept = e.department || e.department_name || e.branch || e.departmentId;
          const preferredRole = e.role || e.position || e.designation || e.job_title;
          const preferredAvatar = e.avatar || e.avatar_url || e.photo_url || e.profile_pic || e.profilePicture;
          const preferredEmail = e.email || e.work_email || e.official_email;
          const preferredPhone = e.phone || e.mobile || e.contact || e.mobile_number || e.phone_number;
          dirOnlyPins.push({
            id, preferredName, preferredDept, preferredRole, preferredAvatar, preferredEmail, preferredPhone,
            _bucketLen: bucket.length,
          });
        } catch (err) {
          const uid = String(e?.id || e?.employee_id || e?.user_id || e?._id || 'unknown');
          // eslint-disable-next-line no-console
          console.warn(`[MapView][DROP] pass=PrePass0 uid=${uid} err=${err?.message || String(err)} headKeys=${JSON.stringify(Object.keys(e || {}).slice(0, 8))}`);
          return;
        }
      });
      // eslint-disable-next-line no-console
      console.info('[MapView][PRE-PASS0] directory-employee candidates filtered by today-first-login:', {
        dirTotal: (empList || []).length,
        dirPassedTodayOnly: dirOnlyPins.length,
        filteredOutByOldLogin: (empList || []).length - dirOnlyPins.length,
        todayKey: todayKeyForPrePass,
      });

      const pickLocation = (loc, fallbackRecord) => {
        const asNum = (x) => (x === null || x === undefined || x === '' ? null : Number(x));
        const tryObject = (obj) => {
          if (!obj || typeof obj !== 'object') return null;
          if (Array.isArray(obj) && obj.length >= 2) {
            const a = asNum(obj[0]);
            const b = asNum(obj[1]);
            if (typeof a === 'number' && typeof b === 'number' && !Number.isNaN(a) && !Number.isNaN(b)) {
              return { lat: a, lng: b, address: '', format: 'array' };
            }
            return null;
          }
          const lat =
            asNum(obj.latitude) ??
            asNum(obj.lat) ??
            asNum(obj.latitudeDegrees) ??
            asNum(obj.lat_deg) ??
            asNum(obj?.gps?.latitude) ??
            asNum(obj?.coords?.latitude);
          const lng =
            asNum(obj.longitude) ??
            asNum(obj.lng) ??
            asNum(obj.lon) ??
            asNum(obj.longitudeDegrees) ??
            asNum(obj.lon_deg) ??
            asNum(obj?.gps?.longitude) ??
            asNum(obj?.coords?.longitude);
          const address =
            (typeof obj.address === 'string' && obj.address.trim()) ||
            (typeof obj.addressLine === 'string' && obj.addressLine.trim()) ||
            (typeof obj.city_name === 'string' && obj.city_name.trim()) ||
            '';
          if (typeof lat === 'number' && typeof lng === 'number' && !Number.isNaN(lat) && !Number.isNaN(lng)) {
            return { lat, lng, address, format: 'object' };
          }
          if (obj.geometry && Array.isArray(obj.geometry.coordinates) && obj.geometry.coordinates.length >= 2) {
            const coords = obj.geometry.coordinates;
            const lngG = asNum(coords[0]);
            const latG = asNum(coords[1]);
            if (typeof latG === 'number' && typeof lngG === 'number' && !Number.isNaN(latG) && !Number.isNaN(lngG)) {
              return { lat: latG, lng: lngG, address, format: 'geojson' };
            }
          }
          return null;
        };

        const candidates = [];
        if (loc) candidates.push(tryObject(loc));
        if (fallbackRecord) {
          candidates.push(tryObject(fallbackRecord.location || fallbackRecord.loc || fallbackRecord.coords));
          const topLevelLat = asNum(fallbackRecord.latitude) ?? asNum(fallbackRecord.lat);
          const topLevelLng = asNum(fallbackRecord.longitude) ?? asNum(fallbackRecord.lng) ?? asNum(fallbackRecord.lon);
          if (typeof topLevelLat === 'number' && typeof topLevelLng === 'number' && !Number.isNaN(topLevelLat) && !Number.isNaN(topLevelLng)) {
            const topAddress = (typeof fallbackRecord.location === 'string' && fallbackRecord.location.trim()) || (typeof fallbackRecord.city_name === 'string' && fallbackRecord.city_name.trim()) || '';
            candidates.push({ lat: topLevelLat, lng: topLevelLng, address: topAddress, format: 'top-level' });
          }
          if (fallbackRecord.coords && typeof fallbackRecord.coords.latitude === 'number' && typeof fallbackRecord.coords.longitude === 'number') {
            candidates.push({ lat: fallbackRecord.coords.latitude, lng: fallbackRecord.coords.longitude, address: '', format: 'coords' });
          }
        }
        const firstOk = candidates.find((c) => c && typeof c.lat === 'number' && typeof c.lng === 'number') || null;
        if (firstOk) {
          return [firstOk.lat, firstOk.lng, firstOk.address || ''];
        }
        // LAST RESORT: text-only location (Clockora attendance stores city name as string)
        const addressText =
          (loc && typeof loc === 'object' && (typeof loc.address === 'string' ? loc.address : typeof loc.city_name === 'string' ? loc.city_name : '')) ||
          (typeof loc === 'string' ? loc.trim() : '') ||
          (fallbackRecord && typeof fallbackRecord.location === 'string' ? fallbackRecord.location.trim() : '') ||
          (fallbackRecord && typeof fallbackRecord.city_name === 'string' ? fallbackRecord.city_name.trim() : '') ||
          (fallbackRecord && typeof fallbackRecord.branch === 'string' ? fallbackRecord.branch.trim() : '') ||
          (fallbackRecord && typeof fallbackRecord.office === 'string' ? fallbackRecord.office.trim() : '') ||
          (fallbackRecord && typeof fallbackRecord.location_note === 'string' ? fallbackRecord.location_note.trim() : '') ||
          '';
        const [cityLat, cityLng] = pickLocationCity(addressText);
        if (typeof cityLat === 'number' && typeof cityLng === 'number') {
          return [cityLat, cityLng, addressText || ''];
        }
        return [null, null, addressText || ''];
      };
      const fmtRel = (ts) => {
        if (!ts) return '';
        try {
          const d = new Date(typeof ts === 'string' ? ts : ts);
          if (Number.isNaN(d.getTime())) return '';
          const diffMs = Date.now() - d.getTime();
          const diffMin = Math.max(0, Math.floor(diffMs / 60000));
          if (diffMin < 1) return 'Just now';
          if (diffMin < 60) return `${diffMin} min${diffMin === 1 ? '' : 's'} ago`;
          const diffHr = Math.floor(diffMin / 60);
          if (diffHr < 24) return `${diffHr} hr${diffHr === 1 ? '' : 's'} ago`;
          const diffDay = Math.floor(diffHr / 24);
          return `${diffDay} day${diffDay === 1 ? '' : 's'} ago`;
        } catch {
          return '';
        }
      };


      const employees = [];
      const seenIds = new Set();

      const pushRecord = (rec, preferredName, preferredId, preferredDept, preferredRole, preferredAvatar, preferredEmail, preferredPhone) => {
        const uid =
          preferredId ||
          rec?.user_id ||
          rec?.employee_id ||
          rec?.employeeId ||
          rec?.emp_id ||
          (rec?._id ? String(rec._id) : null) ||
          (rec?.id ? String(rec.id) : null);
        if (!uid || seenIds.has(uid)) return;
        seenIds.add(uid);

        let records = (byUser.get(uid) || []).slice();

        if (!records.length) {
          const uidCandidates = new Set();
          const pushUid = (x) => { if (x != null && x !== '') uidCandidates.add(String(x)); };
          pushUid(preferredId);
          pushUid(rec?.user_id);
          pushUid(rec?.employee_id);
          pushUid(rec?.employeeId);
          pushUid(rec?.emp_id);
          pushUid(rec?._id ? String(rec._id) : null);
          pushUid(rec?.id ? String(rec.id) : null);
          pushUid(rec?.userId);

          const nameNeedle =
            (typeof preferredName === 'string' && preferredName.trim().toLowerCase()) ||
            (typeof (rec?.user_name || rec?.full_name || rec?.name || rec?.display_name || '') === 'string' && (rec.user_name || rec.full_name || rec.name || rec.display_name || '').trim().toLowerCase()) ||
            '';

          records = attList.filter((r) => {
            if (!r) return false;
            const bucketIds = [
              r.user_id,
              r.employee_id,
              r.employeeId,
              r.emp_id,
              r.userId,
              r._id ? String(r._id) : null,
              r.id ? String(r.id) : null,
            ].filter((x) => x != null && x !== '').map((x) => String(x));
            if (bucketIds.some((b) => uidCandidates.has(b))) return true;
            if (nameNeedle) {
              const hay = (
                (typeof r.full_name === 'string' ? r.full_name : '') +
                ' ' +
                (typeof r.user_name === 'string' ? r.user_name : '') +
                ' ' +
                (typeof r.name === 'string' ? r.name : '') +
                ' ' +
                (typeof r.display_name === 'string' ? r.display_name : '')
              ).toLowerCase();
              if (hay && (hay.includes(nameNeedle) || nameNeedle.includes(hay.trim()))) return true;
            }
            return false;
          });
        }

        records.sort((a, b) => extractTimestampMillis(b) - extractTimestampMillis(a));
        const { todayLatest, sortedDays, byDay } = extractLoginsPerDay(records);

        // STRICT TODAY ONLY: NO pin unless the employee has a first-login record
        // for the PRESENT LOCAL DAY (today). Per canonical rule: last-login fallback
        // and directory-only fallback are NOT permitted here — pins represent
        // attendance confirmations for the current shift day only.
        const anchor = todayLatest;
        const anchorIsToday = Boolean(todayLatest);
        if (!anchor) {
          // eslint-disable-next-line no-console
          console.warn('[MapView][PASS1/2] skip employee pin — NO first-login record for TODAY (strict today-only rule).', {
            uid,
            preferredName,
            recordsLen: records.length,
            sortedDays,
            loginDayKeys: [...(byDay?.keys() || [])],
            sampleRecordKeys: records[0] ? Object.keys(records[0]).slice(0, 40) : null,
          });
          return;
        }

        const [lat, lng, addr] = pickLocation(anchor.location || anchor.loc || {}, anchor);
        let resolvedLat = typeof lat === 'number' ? lat : null;
        let resolvedLng = typeof lng === 'number' ? lng : null;
        let coordsSource = 'parsed_ok';
        if (resolvedLat == null || resolvedLng == null) {
          // No GPS on the today login record (location permission denied / not captured)
          // → no pin. Per requirement: "no location permission = no pin". Skip entirely.
          // eslint-disable-next-line no-console
          console.info('[MapView][PASS1/2] skip pin — today login has no GPS coordinates (no-permission=no-pin rule).', {
            uid, preferredName, pickReturn: [lat, lng, addr],
          });
          return;
        }

        const lastTsMs = extractTimestampMillis(anchor);
        const lastTs = lastTsMs ? new Date(lastTsMs).toISOString() : (anchor.timestamp || anchor.created_at || null);

        const lastOut = (records || []).find((r) => isLogoutTypeRecord(r));
        const inMs = lastTsMs || (lastTs ? new Date(lastTs).getTime() : 0);
        const outMs = lastOut ? extractTimestampMillis(lastOut) : 0;

        let status = 'active';
        const anchorStatus = ((anchor.status || '') + '').toLowerCase();
        if (outMs > inMs) {
          status = 'off_duty';
        } else if (anchorStatus.includes('late')) {
          status = 'on_site';
        } else if (anchorStatus.includes('undertime')) {
          status = 'off_duty';
        }

        const name = preferredName || anchor.user_name || anchor.full_name || anchor.name || anchor.display_name || ('Employee ' + String(uid).slice(-4));
        const parts = name.split(/\s+/).filter(Boolean);
        const initials = parts.length ? (parts[0][0] || '') + (parts[parts.length - 1]?.[0] || '') : '??';
        const avatar = preferredAvatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}&background=1e3a8a&color=facc15&size=256&bold=true&font-size=0.4`;

        const locLabel =
          coordsSource === 'default_ph_center_fallback'
            ? (addr || 'Default map center — no GPS/city coords parsed for today login')
            : (addr || 'Recorded coordinates');
        const lastActiveText = fmtRel(lastTs) || 'Latest login today';
        const taskText = `Latest login location (today): ${locLabel}`;
        employees.push({
          id: `emp-${uid}`,
          name,
          role: preferredRole || anchor.role || anchor.position || anchor.job_title || 'Team Member',
          department: preferredDept || anchor.branch || anchor.department || anchor.office || 'General Staff',
          email: preferredEmail || anchor.email || anchor.official_email || anchor.work_email || '',
          phone: preferredPhone || anchor.phone || anchor.mobile || anchor.mobile_number || anchor.contact || '',
          avatar,
          _avatarInitials: initials,
          lat: resolvedLat,
          lng: resolvedLng,
          status,
          assignedClientId: null,
          battery: null,
          lastActive: lastActiveText,
          currentTask: taskText,
          skills: [],
          _source: coordsSource === 'default_ph_center_fallback' ? 'employee_attendance_fallback_coords' : 'employee_attendance',
          _attendanceUserId: uid,
          _hadCoords: coordsSource !== 'default_ph_center_fallback',
          _lastLoginAt: lastTs || null,
          _lastLoginAddress: locLabel || '',
          _lastLoginToday: true,
          speed: null,
        });
      };

      // ----- ULTRA VERBOSE DEBUG TRACE: actual MongoDB record shape from backend -----
      // eslint-disable-next-line no-console
      console.info('[MapView][TRACE] attendance raw records sample:', {
        total: attList.length,
        firstThreeKeys: attList.slice(0, 3).map((r, idx) => ({ idx, keys: Object.keys(r || {}), id: r?._id || r?.id || r?.user_id || r?.employee_id || null })),
        firstThreeRaw: attList.slice(0, 3).map((r, idx) => {
          if (!r) return null;
          const { password, photo, selfie_photo, premises_photo, image, ...rest } = r;
          return { idx, ...rest };
        }),
      });

      // ----- PASS 1 (DIR-ONLY TODAY-FILTERED — RESTORED per user direction):
      // Iterate dirOnlyPins = every directory employee who has a today-login bucket.
      // pushRecord for each → pins use directory names/avatars/departments (rich display)
      // but attendance's today-first-login anchor (strict rule: coords/time from today login).
      // This renders pins with the "earlier adjustments where all employees are visible"
      // universe, BUT with a filter gate that silently drops yesterday-or-older logins.
      (dirOnlyPins || []).forEach((p) => {
        try {
          if (!p || !p.id) return;
          pushRecord(
            { user_id: p.id },
            p.preferredName || '',
            p.id,
            p.preferredDept || '',
            p.preferredRole || '',
            p.preferredAvatar || '',
            p.preferredEmail || '',
            p.preferredPhone || ''
          );
        } catch (err) {
          const uid = String(p?.id || 'unknown');
          // eslint-disable-next-line no-console
          console.warn(`[MapView][DROP] pass=Pass1 uid=${uid} err=${err?.message || String(err)} headKeys=${JSON.stringify(Object.keys(p || {}).slice(0, 8))}`);
          return;
        }
      });

      // ----- PASS 2 (ATTENDANCE REPRESENTATIVE FALLBACK — strict today):
      // For attendance buckets that have NO corresponding directory entry (not rendered
      // via Pass1), try rendering a pin from the bucket's latest rep record. pushRecord
      // itself demands today anchor, so yesterday/older buckets are silently dropped
      // inside pushRecord (per strict rule).
      [...byUser.entries()].forEach(([uid, recs]) => {
        try {
          if (!recs || !recs.length) return;
          const rep = recs[0];
          pushRecord(
            rep,
            rep.user_name || rep.full_name || rep.name || rep.display_name || '',
            uid,
            rep.branch || rep.department || rep.office,
            rep.role || rep.position || rep.job_title || '',
            rep.avatar || rep.avatar_url || rep.photo_url || rep.profile_pic || '',
            rep.email || rep.official_email || rep.work_email || '',
            rep.phone || rep.mobile || rep.mobile_number || rep.contact || ''
          );
        } catch (err) {
          const bucketUid = String(uid);
          const rep = (recs || [])[0] || {};
          // eslint-disable-next-line no-console
          console.warn(`[MapView][DROP] pass=Pass2 uid=${bucketUid} err=${err?.message || String(err)} headKeys=${JSON.stringify(Object.keys(rep || {}).slice(0, 8))}`);
          return;
        }
      });

      // ----- PASS 3 (STRICT TODAY ONLY, DIRECT BUCKET ITERATION, NO UID CROSS-REF):
      // Iterate EVERY byUser bucket independently of pass1/2 UID resolution.
      // CANONICAL RULE: render a pin IFF the bucket contains at least one login-type record
      // with today's local calendar date. Earliest login (first-time-in) wins.
      // No fallback to latest-on-record; no pin if today has no time-in (even if past
      // logins exist in the bucket).
      const todayKeyGlobalPass3 = ymdLocal(new Date());
      const bucketProcessedIds = new Set(employees.map((e) => String(e._attendanceUserId || e.id || '').replace(/^emp-/, '')));
      [...byUser.entries()].forEach(([bucketUid, bucketRecs]) => {
        try {
          if (bucketProcessedIds.has(String(bucketUid))) return;
          bucketProcessedIds.add(String(bucketUid));
          const loginRecs = (bucketRecs || []).filter((r) => isLoginTypeRecord(r));
          if (!loginRecs.length) return;
          const today = todayKeyGlobalPass3 ? loginRecs.filter((r) => ymdLocal(new Date(extractTimestampMillis(r))) === todayKeyGlobalPass3) : [];
          // STRICT: if no login record for THIS bucket's LOCAL TODAY → skip, no pin.
          if (!today.length) {
            // eslint-disable-next-line no-console
            console.warn('[MapView][PASS3] skip bucket — NO time-in record for TODAY (strict today-only rule).', {
              bucketUid,
              loginRecs: loginRecs.length,
              lastLoginDay: loginRecs.slice().sort((a, b) => extractTimestampMillis(b) - extractTimestampMillis(a))[0]
                ? ymdLocal(new Date(extractTimestampMillis(loginRecs.sort((a, b) => extractTimestampMillis(b) - extractTimestampMillis(a))[0])))
                : null,
            });
            return;
          }
          const sorted = today.slice().sort((a, b) => extractTimestampMillis(b) - extractTimestampMillis(a));
          const anchor = sorted[0];
          if (!anchor) return;
          let [lat, lng, addr] = pickLocation(anchor.location || anchor.loc || {}, anchor);
          let resolvedLat = typeof lat === 'number' ? lat : null;
          let resolvedLng = typeof lng === 'number' ? lng : null;
          let sourceLabel = 'latest_login_today';
          if (resolvedLat == null || resolvedLng == null) {
            // No GPS on the today login record (permission denied / not captured) → no pin.
            // eslint-disable-next-line no-console
            console.info('[MapView][PASS3] skip bucket — today login has no GPS coordinates (no-permission=no-pin rule).', { bucketUid });
            return;
          }
          const recMs = extractTimestampMillis(anchor);
          const lastTs = recMs ? new Date(recMs).toISOString() : (anchor.timestamp || anchor.created_at || null);
          const name =
            (typeof anchor.full_name === 'string' && anchor.full_name.trim()) ||
            (typeof anchor.user_name === 'string' && anchor.user_name.trim()) ||
            (typeof anchor.name === 'string' && anchor.name.trim()) ||
            (typeof anchor.display_name === 'string' && anchor.display_name.trim()) ||
            `Employee ${String(bucketUid).slice(-6)}`;
          const parts = name.split(/\s+/).filter(Boolean);
          const initials = parts.length ? (parts[0][0] || '') + (parts[parts.length - 1]?.[0] || '') : '??';
          const avatar = `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}&background=1e3a8a&color=facc15&size=256&bold=true&font-size=0.4`;
          const lastOut = (bucketRecs || []).find((r) => isLogoutTypeRecord(r));
          const inMs = recMs;
          const outMs = lastOut ? extractTimestampMillis(lastOut) : 0;
          let status = 'active';
          const st = ((anchor.status || '') + '').toLowerCase();
          if (outMs > inMs) status = 'off_duty';
          else if (st.includes('late')) status = 'on_site';
          else if (st.includes('undertime')) status = 'off_duty';
          const safeId = `emp-attpass3-${bucketUid}`;
          const locLabel =
            sourceLabel === 'default_ph_center_fallback'
              ? (addr || 'Default map center — no coords for today first-login')
              : (addr || 'Recorded coordinates');
          employees.push({
            id: safeId,
            name,
            role: anchor.role || anchor.position || anchor.job_title || 'Team Member',
            department: anchor.branch || anchor.department || anchor.office || 'General Staff',
            email: anchor.email || anchor.official_email || anchor.work_email || '',
            phone: anchor.phone || anchor.mobile || anchor.mobile_number || anchor.contact || '',
            avatar,
            _avatarInitials: initials,
            lat: resolvedLat,
            lng: resolvedLng,
            status,
            assignedClientId: null,
            battery: null,
            lastActive: fmtRel(lastTs) || 'Latest login today',
            currentTask: `Latest login location (today): ${locLabel}`,
            skills: [],
            _source: 'employee_attendance_pass3',
            _attendanceUserId: bucketUid,
            _hadCoords: sourceLabel !== 'default_ph_center_fallback',
            _lastLoginAt: lastTs,
            _lastLoginAddress: locLabel || '',
            _lastLoginToday: true,
            speed: null,
          });
        } catch (err) {
          const uid = String(bucketUid);
          const anchor = (bucketRecs || [])[0] || {};
          // eslint-disable-next-line no-console
          console.warn(`[MapView][DROP] pass=Pass3 uid=${uid} err=${err?.message || String(err)} headKeys=${JSON.stringify(Object.keys(anchor || {}).slice(0, 8))}`);
          return;
        }
      });

      // ----- PASS 4 (ZERO employees with a TODAY login):
      // An empty result is a VALID state — no one has logged in today yet, or pins were
      // cleared at 12:01 AM. Do NOT inject any placeholder pin; leave the map empty.
      if (!employees.length) {
        // eslint-disable-next-line no-console
        console.info('[MapView][PASS4] 0 employee pins for today (no time-in login with GPS for the present local day). Map intentionally empty.', {
          attendanceRecords: attList.length,
          attendanceBuckets: byUser.size,
          employeesDirCount: empList.length,
          todayLocal: ymdLocal(new Date()),
        });
      }

      // eslint-disable-next-line no-console
      console.info('[MapView] employee pin pipeline summary:', {
        todayLocal: ymdLocal(new Date()),
        employeesDirCount: empList.length,
        attendanceRecords: attList.length,
        attendanceBuckets: byUser.size,
        dirFallbackPins: dirOnlyPins.length,
        pass1DirPins: Math.min(empList.length, employees.length),
        pinsRendered: employees.length,
        strictRule: 'today_first_login_only',
        pinSources: employees.map((e) => e._source || 'unknown'),
        pinIds: employees.map((e) => e.id),
        pinNames: employees.map((e) => e.name).slice(0, 30),
        byUserKeysSample: [...byUser.keys()].slice(0, 20),
        firstPinLatLng: employees[0] ? [employees[0].lat, employees[0].lng] : null,
        firstPinName: employees[0]?.name || null,
      });

      // FINAL DEDUPE: exactly one pin per employee (latest login wins). The same
      // real person can arrive from TWO sources with different id fields — the
      // legacy `attendance` collection (user_id) and the new `employee_attendance`
      // collection (employee_id) — plus the directory pass. So we key on the
      // person's normalized name (stable across all sources); fall back to the
      // attendance UID only when a name is unavailable.
      const dedup = new Map();
      for (const e of employees) {
        const nameKey = String(e.name || '').trim().toLowerCase();
        const key = nameKey
          ? ('name:' + nameKey)
          : ('uid:' + String(e._attendanceUserId || '').trim());
        const prev = dedup.get(key);
        if (!prev) { dedup.set(key, e); continue; }
        const pt = prev._lastLoginAt ? new Date(prev._lastLoginAt).getTime() : 0;
        const ct = e._lastLoginAt ? new Date(e._lastLoginAt).getTime() : 0;
        if (ct > pt) dedup.set(key, e);
      }
      return [...dedup.values()];
    };

    const msUntilNext1201AM = () => {
      const now = new Date();
      const next = new Date(now);
      next.setDate(next.getDate() + 1);
      next.setHours(0, 1, 0, 0);
      return Math.max(1000, next.getTime() - now.getTime());
    };

    (async () => {
      // Hard deadman: even if an upstream request never resolves (TCP half-open,
      // Cloudflare 520 silent RST, etc.) we MUST unlock the spinner so the user
      // can interact with the page. The per-request axios timeout (30s) is the
      // primary guard, but we keep this 45s global as defense-in-depth.
      let deadFired = false;
      const loadDeadline = setTimeout(() => {
        deadFired = true;
        if (mounted) {
          setLoadError('Map data timed out. Using last-known state.');
          setIsLoading(false);
        }
      }, 45000);

      try {
        const linesResPromise = axiosInstance
          .get('/mappoint/lines')
          .catch(() => ({ data: [] }));
        const [linesRaw] = await Promise.all([linesResPromise]);
        if (!mounted || deadFired) return;
        const lines = Array.isArray(linesRaw?.data) ? linesRaw.data : [];
        setSavedLines(lines);
        const shapedClients = shapeLinesAsClients(lines);
        setClients(shapedClients);
        const shapedEmployees = await buildEmployeesFromAttendance();
        if (!mounted || deadFired) return;
        // An empty array is a VALID result (no one logged in today, or pins were cleared
        // at 12:01 AM). Show exactly what the pipeline resolved — no placeholder pins.
        const safeShapedEmps = Array.isArray(shapedEmployees) ? shapedEmployees : [];
        setEmployees(safeShapedEmps);
        setLoadError(null);
      } catch (e) {
        if (!mounted || deadFired) return;
        const msg = (e && typeof e === 'object' && e.message) ? e.message : 'Unknown load error';
        setLoadError(`Could not load pins: ${msg}`);
        // eslint-disable-next-line no-console
        console.error('[MapView][LOADER] Exception in pin loader.', { exception: e, stack: e && e.stack });
        setEmployees([]);
      } finally {
        clearTimeout(loadDeadline);
        if (mounted && !deadFired) setIsLoading(false);
      }
    })();

    return () => { mounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [axiosInstance, reloadTick]);

  /* --------- DAILY 12:01 AM PURGE SCHEDULER (local-time midnight +1 min): wipe employee pins then refetch --------- */
  useEffect(() => {
    let timer = null;
    let intervalTimer = null;
    const purge = () => {
      setEmployees([]);
      setSelectedEntity((cur) => (cur && cur.type === 'employee' ? null : cur));
      setReloadTick((x) => x + 1);
    };
    const scheduleNext = () => {
      if (timer) { clearTimeout(timer); timer = null; }
      const ms = (() => {
        const now = new Date();
        const next = new Date(now);
        next.setDate(next.getDate() + 1);
        next.setHours(0, 1, 0, 0);
        return Math.max(1000, next.getTime() - now.getTime());
      })();
      timer = setTimeout(() => {
        try { purge(); } catch {}
        scheduleNext();
      }, ms);
    };
    scheduleNext();
    intervalTimer = setInterval(() => {
      const now = new Date();
      if (now.getHours() === 0 && now.getMinutes() === 1 && now.getSeconds() <= 30) {
        try { purge(); } catch {}
      }
    }, 15000);
    return () => {
      if (timer) clearTimeout(timer);
      if (intervalTimer) clearInterval(intervalTimer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* --------- LIVE REFRESH POLL: re-fetch pins every 60s so a new time-in login
     for any employee surfaces on an already-open map (shows the LATEST login). --------- */
  useEffect(() => {
    const poll = setInterval(() => {
      setReloadTick((x) => x + 1);
    }, 60000);
    return () => clearInterval(poll);
  }, []);

  /* --------- RENDER MAPPOLYLINE LAYER (Saved MapPoint lines drawn as polylines UNDER markers, always pinned to linesLayerRef group) --------- */
  useEffect(() => {
    const map = mapRef.current;
    const host = linesLayerRef.current;
    if (!map || !host) return;
    host.clearLayers();
    if (!Array.isArray(savedLines) || savedLines.length === 0) {
      try { host.bringToBack(); } catch {}
      return;
    }
    savedLines.forEach((line) => {
      const pts = Array.isArray(line.points) ? line.points.filter((p) => Array.isArray(p) && p.length >= 2 && typeof p[0] === 'number' && typeof p[1] === 'number') : [];
      if (pts.length < 1) return;
      if (pts.length === 1) {
        const m = L.circleMarker(pts[0], {
          radius: 6,
          color: line.color || '#ef4444',
          weight: 2,
          fillColor: line.color || '#ef4444',
          fillOpacity: 0.35,
        });
        m.bindTooltip(
          `<div style="font-size:10.5px;padding:5px 7px;background:#020617;color:#fff;border-radius:8px;border:1px solid rgba(250,204,21,0.5);">${(line.tag || 'Point').replace(/</g, '&lt;')} (single waypoint)</div>`,
          { direction: 'top', offset: [0, -8], opacity: 0.95 }
        );
        host.addLayer(m);
      } else {
        const pl = L.polyline(pts, {
          color: line.color || '#ef4444',
          weight: Number(line.weight) || 4,
          opacity: line.visible === false ? 0.25 : 0.9,
          lineJoin: 'round',
          lineCap: 'round',
        });
        pl.bindTooltip(
          `<div style="font-size:10.5px;padding:5px 7px;background:#020617;color:#fff;border-radius:8px;border:1px solid rgba(250,204,21,0.5);white-space:nowrap;max-width:260px;overflow:hidden;text-overflow:ellipsis;">${(line.tag || `Route (${pts.length} pts)`).replace(/</g, '&lt;')}</div>`,
          { sticky: true, opacity: 0.95 }
        );
        host.addLayer(pl);
      }
    });
    try {
      host.bringToBack();
    } catch {}
    try {
      markersLayerRef.current?.bringToFront();
    } catch {}
  }, [savedLines]);

  /* --------- AUTOFIT BOUNDS WHEN DATA LOADS FIRST TIME (zoom to fit all pins) --------- */
  useEffect(() => {
    if (isLoading) return;
    const map = mapRef.current;
    if (!map) return;
    const all = [];
    (employees || []).forEach((e) => {
      if (typeof e.lat === 'number' && typeof e.lng === 'number') all.push([e.lat, e.lng]);
    });
    (clients || []).forEach((c) => {
      if (typeof c.lat === 'number' && typeof c.lng === 'number') all.push([c.lat, c.lng]);
    });
    (savedLines || []).forEach((line) => {
      const pts = Array.isArray(line.points) ? line.points.filter((p) => Array.isArray(p) && p.length >= 2 && typeof p[0] === 'number') : [];
      pts.forEach((p) => all.push([p[0], p[1]]));
    });
    if (all.length === 0) return;
    const bounds = L.latLngBounds(all);
    if (bounds.isValid()) {
      try {
        map.fitBounds(bounds, { padding: [70, 70], maxZoom: 12, animate: true, duration: 0.9 });
      } catch {}
    }
    try {
      markersLayerRef.current?.bringToFront();
    } catch {}
  }, [isLoading, employees, clients, savedLines]);

  /* --------- DERIVED: VISIBLE ENTITIES --------- */
  const visibleEmployees = useMemo(() => {
    if (!filterOptions.showEmployees) return [];
    return employees.filter((emp) => {
      const notHidden = !filterOptions.hiddenEmployeeIds.includes(emp.id);
      const statusMatch = filterOptions.employeeStatuses.includes(emp.status);
      const deptMatch =
        filterOptions.departments.length === 0 || filterOptions.departments.includes(emp.department);
      const q = filterOptions.searchQuery.trim().toLowerCase();
      const searchMatch =
        !q ||
        emp.name.toLowerCase().includes(q) ||
        emp.role.toLowerCase().includes(q) ||
        emp.department.toLowerCase().includes(q);
      return notHidden && statusMatch && deptMatch && searchMatch;
    });
  }, [employees, filterOptions]);

  const visibleClients = useMemo(() => {
    if (!filterOptions.showClients) return [];
    return clients.filter((cli) => {
      const notHidden = !filterOptions.hiddenClientIds.includes(cli.id);
      const tierMatch = filterOptions.clientTiers.includes(cli.tier);
      const indMatch =
        filterOptions.industries.length === 0 || filterOptions.industries.includes(cli.industry);
      const q = filterOptions.searchQuery.trim().toLowerCase();
      const searchMatch =
        !q ||
        cli.company.toLowerCase().includes(q) ||
        cli.contactPerson.toLowerCase().includes(q) ||
        cli.city.toLowerCase().includes(q) ||
        cli.industry.toLowerCase().includes(q);
      return notHidden && tierMatch && indMatch && searchMatch;
    });
  }, [clients, filterOptions]);

  /* --------- INIT MAP (matching MapPoint/MapView.js pattern) --------- */
  useEffect(() => {
    if (!mapContainerRef.current || mapRef.current) return undefined;
    const map = L.map(mapContainerRef.current, {
      center: [12.8797, 121.7740],
      zoom: 6,
      minZoom: 5,
      maxZoom: 19,
      zoomControl: false,
      attributionControl: false,
    });
    mapRef.current = map;

    // Native Leaflet zoom control is intentionally disabled — Map View uses its
    // own themed zoom + compass + fullscreen stack below. Keep zoomControl:false
    // on the map constructor so Leaflet's control DOM never overlays the buttons.

    // Match MapPoint's tile layer (OpenStreetMap default, same URL + crossOrigin).
    const tl = L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      crossOrigin: true,
    }).addTo(map);
    tileLayerRef.current = tl;

    const tlErrorHandler = (e) => {
      const now = Date.now();
      lastTileErrorsRef.current.push(now);
      lastTileErrorsRef.current = lastTileErrorsRef.current.slice(-3);
      setTileFailCount((c) => c + 1);
      console.warn('[MapView][TILE] tile load failed', {
        url: e?.tile?.src || (typeof e === 'object' ? e?.target?.src : undefined),
        cumulativeFails: tileFailCount + 1,
      });
      if (
        lastTileErrorsRef.current.length >= 3 &&
        now - lastTileErrorsRef.current[0] <= 5000
      ) {
        toast.warning(
          'Map tiles failed to load. Pins still visible if data loaded.',
          { id: 'tile-fail-warn', duration: 6000 }
        );
      }
    };
    tl.on('tileerror', tlErrorHandler);

    const mapErrorHandler = (e) =>
      console.warn('[MapView][LEAFLET] map error', {
        message: e?.message,
        code: e?.code,
        error: e?.error,
      });
    map.on('error', mapErrorHandler);

    // NOTE: layer group ADD-ORDER matters for z-order in Leaflet's overlayPane.
    // 1) Lines layer FIRST (sits lower in the z stack) → draws under markers
    const linesGroup = L.layerGroup().addTo(map);
    linesLayerRef.current = linesGroup;
    // 2) Markers layer SECOND (higher z stack) → draws on top of lines
    const markersGroup = L.layerGroup().addTo(map);
    markersLayerRef.current = markersGroup;
    // 3) Route polyline layer (will be created/replaced on selection change)
    //    sits between lines and markers if added later; we explicitly raise below.
    // 4) Radius circle layer (also per selection, sits fine between lines & markers).

    map.on('click', (e) => {
      setClickedCoords({ lat: e.latlng.lat, lng: e.latlng.lng });
    });

    // Leaflet needs size recalculation when mounted inside a flex / transitioning
    // container (e.g. PageTransition + sidebar sizing). Without this it renders
    // at 0x0 and the whole page appears empty.
    const resize = () => map.invalidateSize();
    const t1 = setTimeout(resize, 200);
    window.addEventListener('resize', resize);

    return () => {
      clearTimeout(t1);
      window.removeEventListener('resize', resize);
      tl.off('tileerror', tlErrorHandler);
      map.off('error', mapErrorHandler);
      map.remove();
      mapRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* --------- TILE LAYER CHANGE (no-op: single OSM tile matching MapPoint) --------- */
  useEffect(() => {
    // Tile is locked to OSM for parity with MapPoint page.
  }, [activeTileLayerId]);

  /* --------- RENDER MARKERS --------- */
  useEffect(() => {
    const map = mapRef.current;
    const markersGroup = markersLayerRef.current;
    if (!map || !markersGroup) return;
    markersGroup.clearLayers();

    if (filterOptions.showEmployees) {
      visibleEmployees.forEach((emp) => {
        const isSel = selectedEntity?.type === 'employee' && selectedEntity.data.id === emp.id;
        const icon = createEmployeeIcon(emp, isSel);
        const marker = L.marker([emp.lat, emp.lng], { icon, title: emp.name });
        marker.bindTooltip(
          `<div style="font-size:11px;font-weight:600;padding:6px 8px;background:#020617;color:#fff;border-radius:10px;border:1px solid rgba(59,130,246,0.8);box-shadow:0 8px 20px rgba(0,0,0,0.4);">
            <span style="color:#60a5fa;font-weight:700;">● ${emp.name}</span> &bull; ${emp.role}
          </div>`,
          { direction: 'top', offset: [0, -46], opacity: 0.97 }
        );
        marker.on('click', () => {
          setSelectedEntity({ type: 'employee', data: emp });
        });
        markersGroup.addLayer(marker);
      });
    }

    if (filterOptions.showClients) {
      visibleClients.forEach((cli) => {
        const isSel = selectedEntity?.type === 'client' && selectedEntity.data.id === cli.id;
        const icon = createClientIcon(cli, isSel);
        const marker = L.marker([cli.lat, cli.lng], { icon, title: cli.company });
        marker.bindTooltip(
          `<div style="font-size:11px;font-weight:600;padding:6px 8px;background:#020617;color:#fff;border-radius:10px;border:1px solid rgba(250,204,21,0.8);box-shadow:0 8px 20px rgba(0,0,0,0.4);">
            <span style="color:#facc15;font-weight:700;">★ ${cli.company}</span> &bull; ${cli.city}
          </div>`,
          { direction: 'top', offset: [0, -46], opacity: 0.97 }
        );
        marker.on('click', () => {
          setSelectedEntity({ type: 'client', data: cli });
        });
        markersGroup.addLayer(marker);
      });
    }
    try {
      markersGroup.bringToFront();
    } catch {}
    try {
      linesLayerRef.current?.bringToBack();
    } catch {}
    try {
      routeLayerRef.current?.bringToFront();
    } catch {}
  }, [visibleEmployees, visibleClients, filterOptions.showEmployees, filterOptions.showClients, selectedEntity]);

  /* --------- ROUTE LINE --------- */
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (routeLayerRef.current) {
      map.removeLayer(routeLayerRef.current);
      routeLayerRef.current = null;
    }
    let start = null;
    let end = null;
    if (selectedEntity?.type === 'employee') {
      const emp = selectedEntity.data;
      if (emp.assignedClientId) {
        const target = clients.find((c) => c.id === emp.assignedClientId);
        if (target) { start = [emp.lat, emp.lng]; end = [target.lat, target.lng]; }
      }
    } else if (selectedEntity?.type === 'client') {
      const cli = selectedEntity.data;
      if (cli.assignedEmployeeIds && cli.assignedEmployeeIds.length > 0) {
        const emp = employees.find((e) => e.id === cli.assignedEmployeeIds[0]);
        if (emp) { start = [cli.lat, cli.lng]; end = [emp.lat, emp.lng]; }
      }
    }
    if (start && end) {
      const line = L.polyline([start, end], {
        color: '#facc15', weight: 3.5, dashArray: '8, 8', opacity: 0.9,
      }).addTo(map);
      routeLayerRef.current = line;
    }
  }, [selectedEntity, clients, employees]);

  /* --------- RADIUS CIRCLE --------- */
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (radiusLayerRef.current) {
      map.removeLayer(radiusLayerRef.current);
      radiusLayerRef.current = null;
    }
    if (radiusOrigin && radiusKm > 0) {
      const circle = L.circle([radiusOrigin.lat, radiusOrigin.lng], {
        radius: radiusKm * 1000,
        color: '#3b82f6', fillColor: '#facc15', fillOpacity: 0.12, weight: 2, dashArray: '6, 6',
      }).addTo(map);
      radiusLayerRef.current = circle;
    }
  }, [radiusOrigin, radiusKm]);

  /* --------- PAN TO SELECTED --------- */
  useEffect(() => {
    if (!mapRef.current || !selectedEntity) return;
    const { lat, lng } = selectedEntity.data;
    mapRef.current.flyTo([lat, lng], 13.5, { duration: 1.2, easeLinearity: 0.25 });
  }, [selectedEntity]);

  /* --------- CONTROLS --------- */
  const zoomIn = () => mapRef.current?.zoomIn();
  const zoomOut = () => mapRef.current?.zoomOut();
  const resetView = () => mapRef.current?.flyTo([12.8797, 121.7740], 6, { duration: 1 });
  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      mapContainerRef.current?.parentElement?.requestFullscreen?.();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen?.();
      setIsFullscreen(false);
    }
  };

  const handleMapClickPin = (lat, lng) => {
    setClickedCoords({ lat, lng });
    setIsAddModalOpen(true);
  };

  const handleAddEmployee = (newEmp) => {
    setEmployees((prev) => [newEmp, ...prev]);
    setSelectedEntity({ type: 'employee', data: newEmp });
    setFilterOptions((p) => ({ ...p, showEmployees: true }));
  };

  const handleAddClient = (newCli) => {
    setClients((prev) => [newCli, ...prev]);
    setSelectedEntity({ type: 'client', data: newCli });
    setFilterOptions((p) => ({ ...p, showClients: true }));
  };

  const handleApplyRadius = (client, km) => {
    setRadiusOrigin(client);
    setRadiusKm(km);
    setSelectedEntity({ type: 'client', data: client });
  };

  const handleClearRadius = () => setRadiusOrigin(null);

  /* ================================================================ */
  /*                           RENDER                                  */
  /* ================================================================ */

  return (
    <>
      <MapIntroCurtain show={!minElapsed} isLight={isLight} />
      <div
        className="nad-mapview-page w-full h-full"
        style={{ height: 'calc(100vh - 220px)', minHeight: '560px' }}
      >
      <Card3D
        className="w-full h-full p-0 overflow-hidden"
        style={{ borderRadius: '20px' }}
        tilt={false}
      >
        <div className="relative w-full h-full overflow-hidden bg-slate-950 rounded-[20px]">
          <div ref={mapContainerRef} className="w-full h-full" />

          {/* ---------- LOADING OVERLAY ---------- */}
          {isLoading && (
            <div
              className="nad-mapview-ui-control absolute inset-0 z-[3000] flex flex-col items-center justify-center pointer-events-none"
              style={{
                background: 'radial-gradient(ellipse at center, rgba(15,23,42,0.55) 0%, rgba(15,23,42,0.3) 60%, rgba(15,23,42,0.1) 100%)',
                backdropFilter: 'blur(2px)',
              }}
            >
              <div
                className="relative mb-4"
                style={{
                  width: 76,
                  height: 76,
                }}
              >
                <div
                  className="absolute inset-0 rounded-full"
                  style={{
                    border: '3px solid rgba(250,204,21,0.15)',
                  }}
                />
                <div
                  className="absolute inset-0 rounded-full"
                  style={{
                    border: '3px solid transparent',
                    borderTopColor: '#facc15',
                    borderRightColor: '#3b82f6',
                    animation: 'nad-spin 1.1s linear infinite',
                    boxShadow: '0 0 30px -10px rgba(250,204,21,0.7), 0 0 30px -12px rgba(59,130,246,0.55)',
                  }}
                />
                <div
                  className="absolute inset-0 flex items-center justify-center"
                >
                  <div
                    style={{
                      width: 28,
                      height: 28,
                      borderRadius: 10,
                      background: 'linear-gradient(135deg,#eab308 0%,#fbbf24 50%,#fde047 100%)',
                      boxShadow: '0 6px 18px -6px rgba(120,53,15,0.7), inset 0 1px 0 rgba(255,255,255,0.4)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="#020617"><path d="M12 2a7 7 0 00-7 7c0 5.25 7 13 7 13s7-7.75 7-13a7 7 0 00-7-7zm0 9.5a2.5 2.5 0 110-5 2.5 2.5 0 010 5z"/></svg>
                  </div>
                </div>
              </div>
              <div className="text-center">
                <div
                  className="text-[15px] font-extrabold tracking-tight"
                  style={{ color: '#facc15', textShadow: '0 2px 10px rgba(250,204,21,0.4)' }}
                >
                  Loading pins…
                </div>
                <div className="text-[11.5px] text-slate-300 mt-1">
                  Fetching MapPoint routes &amp; employee logout locations
                </div>
              </div>
              <style>{`@keyframes nad-spin { to { transform: rotate(360deg); } }`}</style>
            </div>
          )}

          {/* ---------- ERROR OVERLAY ---------- */}
          {!isLoading && loadError && (
            <div
              className="nad-mapview-ui-control absolute inset-0 z-[3000] flex items-center justify-center"
              style={{
                background: 'radial-gradient(ellipse at center, rgba(127,29,29,0.45) 0%, rgba(15,23,42,0.5) 55%, rgba(15,23,42,0.25) 100%)',
                backdropFilter: 'blur(3px)',
              }}
            >
              <div
                className="relative max-w-[420px] w-[86%] rounded-2xl p-5 text-slate-100 shadow-2xl overflow-hidden"
                style={{
                  background: 'linear-gradient(135deg, rgba(127,29,29,0.85) 0%, rgba(15,23,42,0.92) 55%, rgba(30,41,59,0.9) 100%)',
                  border: '1px solid rgba(250,204,21,0.28)',
                  boxShadow: '0 25px 60px -15px rgba(0,0,0,0.75), inset 0 1px 0 rgba(255,255,255,0.06)',
                }}
              >
                <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full pointer-events-none" style={{ background: 'radial-gradient(circle, rgba(250,204,21,0.18), transparent 60%)' }} />
                <div className="relative flex items-start gap-3">
                  <div
                    className="w-11 h-11 rounded-xl shrink-0 flex items-center justify-center"
                    style={{
                      background: 'linear-gradient(135deg, #ef4444 0%, #b91c1c 100%)',
                      boxShadow: '0 8px 20px -8px rgba(127,29,29,0.8), inset 0 1px 0 rgba(255,255,255,0.18)',
                    }}
                  >
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="text-[15.5px] font-extrabold tracking-tight text-yellow-300 drop-shadow">
                      Couldn&apos;t load map data
                    </h4>
                    <p className="text-[11.5px] text-slate-300 mt-1 leading-relaxed break-words">
                      {loadError}
                    </p>
                    <p className="text-[10.5px] text-slate-400 mt-1.5 italic">
                      You can still add pins manually via Display Filter → Add Pin.
                    </p>
                    <div className="flex items-center gap-2 mt-3.5 flex-wrap">
                      <button
                        onClick={() => setReloadTick((x) => x + 1)}
                        className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-[12px] font-bold text-white transition-all hover:scale-[1.02] active:scale-[0.98]"
                        style={{
                          background: 'linear-gradient(135deg,#eab308 0%,#fbbf24 50%,#fde047 100%)',
                          boxShadow: '0 8px 18px -8px rgba(120,53,15,0.7), inset 0 1px 0 rgba(255,255,255,0.35)',
                        }}
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/><path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15"/></svg>
                        Retry load
                      </button>
                      <button
                        onClick={() => setLoadError(null)}
                        className="px-3 py-2 rounded-xl text-[12px] font-semibold text-slate-200 bg-white/5 hover:bg-white/10 border border-white/10 transition-all"
                      >
                        Dismiss
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ---------- TOP RIGHT: DISPLAY FILTER + COUNTER ---------- */}
          <div className="nad-mapview-ui-control absolute top-3 right-3 z-[2000] flex flex-col items-end gap-1.5">
            <div className="relative">
              <div
                role="group"
                aria-label="Map visibility controls"
                className="group relative flex items-center gap-2 pr-3 pl-2 py-1.5 rounded-full overflow-hidden shadow-[0_10px_40px_-8px_rgba(15,23,42,0.9)] border transition-all hover:scale-[1.01]"
                style={{
                  background: 'linear-gradient(135deg, rgba(30,58,138,0.95) 0%, rgba(30,64,175,0.94) 50%, rgba(37,99,235,0.92) 100%)',
                  borderColor: 'rgba(250,204,21,0.55)',
                  boxShadow:
                    '0 0 0 1px rgba(250,204,21,0.25), 0 14px 40px -10px rgba(15,23,42,0.95), 0 0 60px -24px rgba(250,204,21,0.3)',
                }}
                title="Map visibility controls"
              >
                <button
                  onClick={() => setFilterOpen((v) => !v)}
                  tabIndex={0}
                  className="flex items-center gap-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-yellow-300/70 focus-visible:ring-offset-2 focus-visible:ring-offset-blue-900 rounded-full transition-all"
                >
                  <div
                    className="w-8 h-8 rounded-xl flex items-center justify-center shrink-0 relative"
                    style={{
                      background: 'radial-gradient(circle at 30% 20%, rgba(59,130,246,0.85), rgba(30,58,138,0.9) 70%)',
                      boxShadow:
                        'inset 0 1px 0 rgba(255,255,255,0.25), 0 6px 16px -6px rgba(15,23,42,0.9), 0 0 0 1px rgba(250,204,21,0.32)',
                    }}
                  >
                    <Layers className="text-yellow-300" style={{ width: 16, height: 16 }} strokeWidth={2.2} />
                  </div>
                  <div className="flex flex-col items-start leading-tight">
                    <div className="flex items-center gap-1.5">
                      <span className="text-[10px] font-black uppercase tracking-[0.14em] text-yellow-300 drop-shadow">
                        Display Filter
                      </span>
                      <span
                        className="px-2 py-0 rounded-full text-[10px] font-black text-white"
                        style={{
                          background:
                            'linear-gradient(135deg, rgba(59,130,246,0.9) 0%, rgba(37,99,235,0.95) 100%)',
                          boxShadow:
                            'inset 0 1px 0 rgba(255,255,255,0.25), 0 4px 10px -6px rgba(15,23,42,0.9)',
                        }}
                      >
                        {visibleEmployees.length + visibleClients.length} Pins
                      </span>
                    </div>
                    <div className="flex items-center gap-1.5 mt-0.5">
                      <span className="text-white font-extrabold tracking-tight text-[12.5px]">
                        Employees & Clients
                      </span>
                      <svg
                        width="12"
                        height="12"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="white"
                        strokeWidth="3"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        style={{
                          transform: filterOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                          transition: 'transform 220ms cubic-bezier(0.22,1,0.36,1)',
                          marginLeft: 2,
                          opacity: 0.85,
                        }}
                      >
                        <polyline points="6 9 12 15 18 9" />
                      </svg>
                    </div>
                  </div>
                </button>
                <div className="flex items-center gap-0.5 ml-0.5">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setFilterOptions((p) => ({ ...p, showEmployees: !p.showEmployees }));
                    }}
                    className={`w-4 h-4 rounded-full border-2 transition-all flex items-center justify-center ${
                      filterOptions.showEmployees
                        ? 'bg-blue-500 border-blue-300'
                        : 'bg-slate-700/70 border-slate-500'
                    }`}
                    title="Toggle employees on map"
                  >
                    <div className="w-1.5 h-1.5 rounded-full bg-white" />
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setFilterOptions((p) => ({ ...p, showClients: !p.showClients }));
                    }}
                    className={`w-4 h-4 rounded-full border-2 transition-all flex items-center justify-center ${
                      filterOptions.showClients
                        ? 'bg-yellow-400 border-yellow-200'
                        : 'bg-slate-700/70 border-slate-500'
                    }`}
                    title="Toggle clients on map"
                  >
                    <div className="w-1.5 h-1.5 rounded-full bg-slate-900" />
                  </button>
                </div>
              </div>

              {filterOpen && (
                <FilterDropdown
                  open={filterOpen}
                  onToggle={() => setFilterOpen((v) => !v)}
                  filterOptions={filterOptions}
                  setFilterOptions={setFilterOptions}
                  employees={employees}
                  clients={clients}
                  onAdd={() => setIsAddModalOpen(true)}
                  onRadius={() => setIsRadiusModalOpen(true)}
                  onHelp={() => setIsHelpModalOpen(true)}
                  isSimulating={isSimulating}
                  onToggleSim={() => setIsSimulating((v) => !v)}
                />
              )}
            </div>
          </div>

          {/* ---------- BOTTOM RIGHT: ZOOM + RESET + FULLSCREEN ---------- */}
          <div className="nad-mapview-ui-control absolute bottom-6 right-4 z-[2000] flex flex-col items-center gap-2">
            <div className="bg-slate-900/90 backdrop-blur-md rounded-2xl border border-slate-800 p-1 shadow-2xl flex flex-col gap-1">
              <button onClick={zoomIn} className="w-9 h-9 rounded-xl flex items-center justify-center text-slate-200 hover:text-yellow-400 hover:bg-slate-800 transition-all" title="Zoom In">
                <Plus className="w-4 h-4" />
              </button>
              <div className="h-px bg-slate-800 w-6 mx-auto" />
              <button onClick={zoomOut} className="w-9 h-9 rounded-xl flex items-center justify-center text-slate-200 hover:text-yellow-400 hover:bg-slate-800 transition-all" title="Zoom Out">
                <Minus className="w-4 h-4" />
              </button>
            </div>
            <button onClick={resetView} className="w-10 h-10 rounded-2xl bg-slate-900/90 backdrop-blur-md border border-slate-800 flex items-center justify-center text-slate-200 hover:text-yellow-400 hover:border-yellow-400 hover:bg-slate-800 transition-all shadow-xl" title="Reset Map Center">
              <Compass className="w-5 h-5 text-blue-400" />
            </button>
            <button onClick={toggleFullscreen} className="w-10 h-10 rounded-2xl bg-slate-900/90 backdrop-blur-md border border-slate-800 flex items-center justify-center text-slate-200 hover:text-yellow-400 hover:bg-slate-800 transition-all shadow-xl" title="Toggle Fullscreen">
              {isFullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
            </button>
          </div>

          {/* ---------- BOTTOM LEFT: LEGEND ---------- */}
          <div className="nad-mapview-ui-control absolute bottom-6 left-4 z-[1990] hidden sm:flex items-center gap-3 bg-slate-950/85 backdrop-blur-md px-4 py-2.5 rounded-2xl border border-yellow-400/30 shadow-2xl">
            <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400 pr-1">Legend:</div>
            <div className="flex items-center gap-2">
              <div className="w-3.5 h-3.5 rounded-md bg-blue-600 border border-yellow-400/80 shadow-sm flex items-center justify-center">
                <span className="text-[7px] font-bold text-white">E</span>
              </div>
              <span className="text-xs font-semibold text-slate-200">Employees ({visibleEmployees.length})</span>
            </div>
            <div className="h-3 w-px bg-slate-800" />
            <div className="flex items-center gap-2">
              <div className="w-3.5 h-3.5 rounded-md bg-yellow-400 border border-blue-950 shadow-sm flex items-center justify-center">
                <span className="text-[7px] font-black text-white">C</span>
              </div>
              <span className="text-xs font-semibold text-slate-200">Clients ({visibleClients.length})</span>
            </div>
          </div>

          {/* ---------- BOTTOM CENTER: SELECTED ENTITY CARD ---------- */}
          {selectedEntity && <SelectedCard entity={selectedEntity} onClose={() => setSelectedEntity(null)} />}
        </div>

        {/* ---------- MODALS ---------- */}
        <AddEntityModal
          isOpen={isAddModalOpen}
          onClose={() => { setIsAddModalOpen(false); setClickedCoords(null); }}
          onAddEmployee={handleAddEmployee}
          onAddClient={handleAddClient}
          initialCoords={clickedCoords}
        />
        <RadiusModal
          isOpen={isRadiusModalOpen}
          onClose={() => setIsRadiusModalOpen(false)}
          clients={clients}
          onApply={handleApplyRadius}
          onClear={handleClearRadius}
          currentClient={radiusOrigin}
          currentKm={radiusKm}
        />
        <HelpModal isOpen={isHelpModalOpen} onClose={() => setIsHelpModalOpen(false)} />
      </Card3D>
      </div>
    </>
  );
}

/* ================================================================== */
/*                    SUB-COMPONENTS (INLINE)                         */
/* ================================================================== */

function FilterDropdown({
  open, onToggle, filterOptions, setFilterOptions,
  employees, clients, onAdd, onRadius, onHelp,
  isSimulating, onToggleSim,
}) {
  const [activeTab, setActiveTab] = useState('employees');

  const setField = (k, v) => setFilterOptions((p) => ({ ...p, [k]: v }));
  const setHiddenEmps = (updater) => setFilterOptions((p) => ({
    ...p,
    hiddenEmployeeIds: typeof updater === 'function' ? updater(p.hiddenEmployeeIds) : updater,
  }));
  const setHiddenClients = (updater) => setFilterOptions((p) => ({
    ...p,
    hiddenClientIds: typeof updater === 'function' ? updater(p.hiddenClientIds) : updater,
  }));

  const q = (filterOptions.searchQuery || '').trim().toLowerCase();
  const matchingEmployees = useMemo(() => {
    return employees.filter((e) => {
      if (
        filterOptions.employeeStatuses.length > 0 &&
        !filterOptions.employeeStatuses.includes(e.status)
      ) return false;
      if (
        filterOptions.departments.length > 0 &&
        !filterOptions.departments.includes(e.department)
      ) return false;
      if (!q) return true;
      return (
        e.name.toLowerCase().includes(q) ||
        e.role.toLowerCase().includes(q) ||
        e.department.toLowerCase().includes(q) ||
        (e.currentTask || '').toLowerCase().includes(q) ||
        (e.email || '').toLowerCase().includes(q)
      );
    });
  }, [employees, q, filterOptions.employeeStatuses, filterOptions.departments]);

  const matchingClients = useMemo(() => {
    return clients.filter((c) => {
      if (
        filterOptions.clientTiers.length > 0 &&
        !filterOptions.clientTiers.includes(c.tier)
      ) return false;
      if (
        filterOptions.industries.length > 0 &&
        !filterOptions.industries.includes(c.industry)
      ) return false;
      if (!q) return true;
      return (
        c.company.toLowerCase().includes(q) ||
        c.city.toLowerCase().includes(q) ||
        c.industry.toLowerCase().includes(q) ||
        c.tier.toLowerCase().includes(q) ||
        (c.contactPerson || '').toLowerCase().includes(q) ||
        (c.address || '').toLowerCase().includes(q)
      );
    });
  }, [clients, q, filterOptions.clientTiers, filterOptions.industries]);

  const visibleEmpsAfterTab = matchingEmployees.filter(
    (e) => !filterOptions.hiddenEmployeeIds.includes(e.id)
  );
  const visibleClisAfterTab = matchingClients.filter(
    (c) => !filterOptions.hiddenClientIds.includes(c.id)
  );
  const empShown = visibleEmpsAfterTab.length;
  const empTotal = matchingEmployees.length;
  const cliShown = visibleClisAfterTab.length;
  const cliTotal = matchingClients.length;

  const toggleEmp = (id) =>
    setHiddenEmps((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  const toggleCli = (id) =>
    setHiddenClients((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );

  const showAllEmps = () => setHiddenEmps([]);
  const hideAllEmps = () => setHiddenEmps(matchingEmployees.map((e) => e.id));
  const showAllClis = () => setHiddenClients([]);
  const hideAllClis = () => setHiddenClients(matchingClients.map((c) => c.id));

  const statusStyles = (status) => {
    switch (status) {
      case 'active': return { bg: 'rgba(34,197,94,0.18)', fg: '#4ade80', border: 'rgba(34,197,94,0.45)', dot: '#22c55e' };
      case 'in_transit': return { bg: 'rgba(56,189,248,0.18)', fg: '#38bdf8', border: 'rgba(56,189,248,0.45)', dot: '#38bdf8' };
      case 'on_site': return { bg: 'rgba(250,204,21,0.18)', fg: '#facc15', border: 'rgba(250,204,21,0.45)', dot: '#facc15' };
      case 'available': return { bg: 'rgba(96,165,250,0.18)', fg: '#60a5fa', border: 'rgba(96,165,250,0.45)', dot: '#60a5fa' };
      case 'off_duty':
      default: return { bg: 'rgba(148,163,184,0.18)', fg: '#94a3b8', border: 'rgba(148,163,184,0.45)', dot: '#94a3b8' };
    }
  };

  const tierStyles = (tier) => {
    switch (tier) {
      case 'Enterprise': return { bg: 'rgba(59,130,246,0.22)', fg: '#bfdbfe', border: 'rgba(59,130,246,0.5)' };
      case 'VIP': return { bg: 'rgba(250,204,21,0.25)', fg: '#fde68a', border: 'rgba(250,204,21,0.55)' };
      case 'Commercial': return { bg: 'rgba(234,179,8,0.18)', fg: '#fde68a', border: 'rgba(234,179,8,0.45)' };
      case 'Prospect':
      default: return { bg: 'rgba(148,163,184,0.18)', fg: '#cbd5e1', border: 'rgba(148,163,184,0.4)' };
    }
  };

  if (!open) return null;

  return (
    <div
      className="nad-mapview-ui-control absolute right-0 mt-1.5 w-[340px] max-w-[90vw] rounded-[22px] overflow-hidden text-slate-100 shadow-2xl z-[2600]"
      style={{
        background: 'linear-gradient(180deg, rgba(15,23,42,0.98) 0%, rgba(15,23,42,0.96) 45%, rgba(30,41,59,0.98) 100%)',
        border: '1.25px solid rgba(250,204,21,0.42)',
        boxShadow:
          '0 0 0 1px rgba(250,204,21,0.15), 0 24px 50px -16px rgba(0,0,0,0.85), 0 0 60px -22px rgba(250,204,21,0.22), inset 0 1px 0 rgba(255,255,255,0.04)',
        animation: 'nad-fade-in-up 200ms cubic-bezier(0.22,1,0.36,1) both',
        maxHeight: 'calc(100vh - 250px)',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* ---------- HEADER ---------- */}
      <div
        className="flex items-start justify-between gap-2.5 px-4 py-3"
        style={{
          background:
            'linear-gradient(135deg, rgba(30,58,138,0.55) 0%, rgba(30,64,175,0.40) 50%, rgba(15,23,42,0) 100%)',
          borderBottom: '1px solid rgba(59,130,246,0.22)',
        }}
      >
        <div className="flex items-start gap-2.5 min-w-0">
          <div
            className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0"
            style={{
              background: 'linear-gradient(145deg, rgba(30,58,138,0.95) 0%, rgba(37,99,235,0.85) 100%)',
              border: '1px solid rgba(250,204,21,0.42)',
              boxShadow:
                'inset 0 1px 0 rgba(255,255,255,0.14), 0 8px 20px -9px rgba(15,23,42,0.9)',
            }}
          >
            <Layers className="w-4 h-4 text-yellow-300" strokeWidth={2.1} />
          </div>
          <div className="min-w-0">
            <h3 className="text-white font-black tracking-tight text-[15.5px] leading-tight">
              Map Visibility Controls
            </h3>
            <p className="text-slate-400 text-[11px] font-medium mt-0.5">
              Toggle individual pins on the map
            </p>
          </div>
        </div>
        <button
          onClick={onToggle}
          className="w-7 h-7 rounded-lg flex items-center justify-center text-slate-300 hover:text-white hover:bg-white/10 border border-white/10 transition-all shrink-0"
          title="Close visibility panel"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* ---------- BODY (scroll) ---------- */}
      <div className="px-3.5 py-3 overflow-y-auto" style={{ scrollbarWidth: 'thin' }}>
        {/* TABS */}
        <div className="grid grid-cols-2 gap-1.5 p-1 rounded-xl bg-slate-950/70 border border-slate-800">
          <button
            onClick={() => setActiveTab('employees')}
            className={`flex items-center justify-center gap-1.5 rounded-lg px-2 py-2 text-[12.5px] font-black transition-all ${
              activeTab === 'employees'
                ? 'text-white'
                : 'text-slate-400 hover:text-slate-200'
            }`}
            style={
              activeTab === 'employees'
                ? {
                    background: 'linear-gradient(135deg, #2563eb 0%, #3b82f6 100%)',
                    boxShadow:
                      '0 8px 20px -10px rgba(37,99,235,0.9), inset 0 1px 0 rgba(255,255,255,0.2)',
                    border: '1px solid rgba(191,219,254,0.35)',
                  }
                : undefined
            }
          >
            <Users style={{ width: 14, height: 14 }} />
            <span className="tracking-tight">Employees</span>
            <span
              className={`px-1.5 py-0 rounded-md text-[10px] font-black ${
                activeTab === 'employees' ? 'bg-blue-900/70 text-blue-100' : 'bg-slate-800 text-slate-300'
              }`}
            >
              {empShown}/{empTotal}
            </span>
          </button>
          <button
            onClick={() => setActiveTab('clients')}
            className={`flex items-center justify-center gap-1.5 rounded-lg px-2 py-2 text-[12.5px] font-black transition-all ${
              activeTab === 'clients'
                ? 'text-slate-100'
                : 'text-slate-400 hover:text-slate-200'
            }`}
            style={
              activeTab === 'clients'
                ? {
                    background: 'linear-gradient(135deg, rgba(148,163,184,0.18) 0%, rgba(51,65,85,0.38) 100%)',
                    border: '1px solid rgba(148,163,184,0.35)',
                  }
                : undefined
            }
          >
            <Building2 style={{ width: 14, height: 14 }} />
            <span className="tracking-tight">Clients</span>
            <span
              className={`px-1.5 py-0 rounded-md text-[10px] font-black ${
                activeTab === 'clients' ? 'bg-slate-900/80 text-slate-200' : 'bg-slate-800 text-slate-300'
              }`}
            >
              {cliShown}/{cliTotal}
            </span>
          </button>
        </div>

        {/* SEARCH */}
        <div
          className="mt-2.5 flex items-center gap-2 px-3 py-2 rounded-xl border"
          style={{
            background: 'rgba(2,6,23,0.6)',
            borderColor: 'rgba(30,58,138,0.55)',
            boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.03)',
          }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#93c5fd" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="7" />
            <path d="M21 21l-4.3-4.3" />
          </svg>
          <input
            type="text"
            placeholder={
              activeTab === 'employees' ? 'Search employees...' : 'Search clients...'
            }
            value={filterOptions.searchQuery}
            onChange={(e) => setField('searchQuery', e.target.value)}
            className="flex-1 bg-transparent text-[12.5px] text-slate-100 placeholder:text-slate-500 outline-none font-medium"
          />
        </div>

        {/* SHOWN + SHOW/HIDE ALL */}
        <div className="mt-2.5 flex items-center justify-between gap-2">
          <div className="text-[10px] font-black tracking-wider uppercase text-slate-400">
            {activeTab === 'employees'
              ? `${empShown} OF ${empTotal} SHOWN`
              : `${cliShown} OF ${cliTotal} SHOWN`}
          </div>
          <div className="flex items-center gap-0.5">
            <button
              onClick={activeTab === 'employees' ? showAllEmps : showAllClis}
              className="flex items-center gap-1 px-2 py-0.5 rounded-lg text-[11px] font-black text-blue-300 hover:text-white hover:bg-blue-500/15 transition-all"
              title={activeTab === 'employees' ? 'Show all employees' : 'Show all clients'}
            >
              <Check style={{ width: 12, height: 12 }} strokeWidth={2.8} /> Show All
            </button>
            <button
              onClick={activeTab === 'employees' ? hideAllEmps : hideAllClis}
              className="flex items-center gap-1 px-2 py-0.5 rounded-lg text-[11px] font-black text-slate-400 hover:text-red-300 hover:bg-red-500/10 transition-all"
              title={activeTab === 'employees' ? 'Hide all employees' : 'Hide all clients'}
            >
              <X style={{ width: 12, height: 12 }} strokeWidth={2.8} /> Hide All
            </button>
          </div>
        </div>

        {/* LIST */}
        <div className="mt-2 space-y-1.5 pr-0.5">
          {activeTab === 'employees' ? (
            matchingEmployees.map((e) => {
              const isOn = !filterOptions.hiddenEmployeeIds.includes(e.id);
              const s = statusStyles(e.status);
              return (
                <div
                  key={e.id}
                  className="flex items-center gap-2.5 px-2.5 py-2 rounded-xl border transition-all"
                  style={{
                    background: 'linear-gradient(180deg, rgba(30,58,138,0.16) 0%, rgba(15,23,42,0.38) 100%)',
                    borderColor: isOn ? 'rgba(59,130,246,0.45)' : 'rgba(71,85,105,0.35)',
                    boxShadow: isOn
                      ? 'inset 0 1px 0 rgba(255,255,255,0.05), 0 6px 18px -14px rgba(37,99,235,0.85)'
                      : 'inset 0 1px 0 rgba(255,255,255,0.03)',
                    opacity: isOn ? 1 : 0.55,
                  }}
                >
                  <div className="relative shrink-0">
                    <img
                      src={e.avatar}
                      alt={e.name}
                      className="w-9 h-9 rounded-xl object-cover border-2"
                      style={{ borderColor: isOn ? 'rgba(59,130,246,0.75)' : 'rgba(71,85,105,0.55)' }}
                    />
                    <span
                      className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-[2px] border-slate-950"
                      style={{ background: s.dot }}
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <div className="text-[13px] font-black text-white truncate leading-tight">
                        {e.name}
                      </div>
                      <span
                        className="px-1.5 py-0 rounded-md text-[9.5px] font-black uppercase tracking-wider border"
                        style={{
                          background: s.bg,
                          color: s.fg,
                          borderColor: s.border,
                        }}
                      >
                        {String(e.status).replace('_', ' ')}
                      </span>
                    </div>
                    <div className="text-[11px] text-slate-300 font-medium mt-0.5 truncate">
                      {e.role}
                    </div>
                  </div>
                  <button
                    onClick={() => toggleEmp(e.id)}
                    role="switch"
                    aria-checked={isOn}
                    className="relative shrink-0 w-11 h-6 rounded-full transition-all"
                    style={{
                      background: isOn
                        ? 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)'
                        : 'linear-gradient(135deg, #334155 0%, #1e293b 100%)',
                      boxShadow: isOn
                        ? 'inset 0 1px 0 rgba(255,255,255,0.2), 0 6px 14px -9px rgba(37,99,235,0.95)'
                        : 'inset 0 1px 0 rgba(255,255,255,0.05)',
                      border: '1px solid rgba(255,255,255,0.08)',
                    }}
                    title={isOn ? 'Hide pin' : 'Show pin'}
                  >
                    <span
                      className="absolute top-1/2 -translate-y-1/2 w-4.5 h-4.5 rounded-full bg-white shadow-md transition-all"
                      style={{
                        left: isOn ? 'calc(100% - 20px)' : '3px',
                        width: 18,
                        height: 18,
                        boxShadow: '0 2px 5px rgba(0,0,0,0.35), inset 0 1px 0 rgba(255,255,255,0.8)',
                      }}
                    />
                  </button>
                </div>
              );
            })
          ) : (
            matchingClients.map((c) => {
              const isOn = !filterOptions.hiddenClientIds.includes(c.id);
              const t = tierStyles(c.tier);
              const initials = (c.company || '').split(/\s+/).filter(Boolean).slice(0,2).map((s) => s[0]).join('').toUpperCase();
              return (
                <div
                  key={c.id}
                  className="flex items-center gap-2.5 px-2.5 py-2 rounded-xl border transition-all"
                  style={{
                    background: 'linear-gradient(180deg, rgba(250,204,21,0.07) 0%, rgba(15,23,42,0.38) 100%)',
                    borderColor: isOn ? 'rgba(250,204,21,0.36)' : 'rgba(71,85,105,0.35)',
                    boxShadow: isOn
                      ? 'inset 0 1px 0 rgba(255,255,255,0.05), 0 6px 18px -14px rgba(250,204,21,0.7)'
                      : 'inset 0 1px 0 rgba(255,255,255,0.03)',
                    opacity: isOn ? 1 : 0.55,
                  }}
                >
                  <div
                    className="w-9 h-9 shrink-0 rounded-xl flex flex-col items-center justify-center border-2"
                    style={{
                      background: 'linear-gradient(145deg, rgba(161,98,7,0.95) 0%, rgba(250,204,21,0.85) 100%)',
                      borderColor: isOn ? 'rgba(250,204,21,0.85)' : 'rgba(120,113,108,0.55)',
                      boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.2)',
                    }}
                  >
                    <Building2 className="text-white" strokeWidth={2.4} style={{ width: 14, height: 14 }} />
                    <span className="text-[8.5px] font-black text-white tracking-tight leading-none mt-0.5">
                      {initials}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <div className="text-[12.5px] font-black text-white truncate leading-tight">
                        {c.company}
                      </div>
                      <span
                        className="px-1.5 py-0 rounded-md text-[9.5px] font-black uppercase tracking-wider border"
                        style={{ background: t.bg, color: t.fg, borderColor: t.border }}
                      >
                        {c.tier}
                      </span>
                    </div>
                    <div className="text-[10.5px] text-slate-300 font-medium mt-0.5 truncate">
                      {c.industry} • {c.city}
                    </div>
                  </div>
                  <button
                    onClick={() => toggleCli(c.id)}
                    role="switch"
                    aria-checked={isOn}
                    className="relative shrink-0 w-11 h-6 rounded-full transition-all"
                    style={{
                      background: isOn
                        ? 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)'
                        : 'linear-gradient(135deg, #334155 0%, #1e293b 100%)',
                      boxShadow: isOn
                        ? 'inset 0 1px 0 rgba(255,255,255,0.2), 0 6px 14px -9px rgba(37,99,235,0.95)'
                        : 'inset 0 1px 0 rgba(255,255,255,0.05)',
                      border: '1px solid rgba(255,255,255,0.08)',
                    }}
                    title={isOn ? 'Hide pin' : 'Show pin'}
                  >
                    <span
                      className="absolute top-1/2 -translate-y-1/2 rounded-full bg-white shadow-md transition-all"
                      style={{
                        left: isOn ? 'calc(100% - 20px)' : '3px',
                        width: 18,
                        height: 18,
                        boxShadow: '0 2px 5px rgba(0,0,0,0.35), inset 0 1px 0 rgba(255,255,255,0.8)',
                      }}
                    />
                  </button>
                </div>
              );
            })
          )}

          {((activeTab === 'employees' && matchingEmployees.length === 0) ||
            (activeTab === 'clients' && matchingClients.length === 0)) && (
            <div className="py-6 text-center text-slate-400 text-xs">
              No {activeTab} match your filters.
            </div>
          )}
        </div>
      </div>

      {/* ---------- FOOTER ---------- */}
      <div
        className="px-3.5 py-2.5 border-t flex items-center gap-2 flex-wrap"
        style={{
          borderColor: 'rgba(148,163,184,0.16)',
          background: 'linear-gradient(180deg, rgba(15,23,42,0) 0%, rgba(2,6,23,0.55) 100%)',
        }}
      >
        <button
          onClick={onAdd}
          className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl font-black text-white transition-all flex-1 min-w-[90px]"
          style={{
            background: 'linear-gradient(135deg, #facc15 0%, #f59e0b 100%)',
            boxShadow:
              'inset 0 1px 0 rgba(255,255,255,0.35), 0 10px 22px -12px rgba(250,204,21,0.85), 0 0 0 1px rgba(161,98,7,0.55)',
          }}
          title="Add a new pin"
        >
          <Plus style={{ width: 14, height: 14 }} strokeWidth={2.6} />
          <span className="text-[12px] tracking-tight">Add Pin</span>
        </button>
        <button
          onClick={onRadius}
          className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl font-black text-yellow-300 hover:text-white transition-all flex-1 min-w-[90px]"
          style={{
            background: 'linear-gradient(135deg, rgba(51,65,85,0.75) 0%, rgba(15,23,42,0.85) 100%)',
            border: '1px solid rgba(250,204,21,0.28)',
            boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.06)',
          }}
          title="Radius proximity tool"
        >
          <Radar className="text-yellow-300" style={{ width: 14, height: 14 }} strokeWidth={2.1} />
          <span className="text-[12px] tracking-tight">Radar</span>
        </button>
        <button
          onClick={onToggleSim}
          className={`flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl font-black transition-all flex-1 min-w-[100px] ${
            isSimulating ? 'text-emerald-200' : 'text-yellow-300'
          }`}
          style={
            isSimulating
              ? {
                  background: 'linear-gradient(135deg, rgba(16,185,129,0.30) 0%, rgba(5,46,22,0.6) 100%)',
                  border: '1px solid rgba(52,211,153,0.45)',
                  boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.08), 0 0 22px -10px rgba(16,185,129,0.5)',
                }
              : {
                  background: 'linear-gradient(135deg, rgba(51,65,85,0.75) 0%, rgba(15,23,42,0.85) 100%)',
                  border: '1px solid rgba(250,204,21,0.28)',
                  boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.06)',
                }
          }
          title={isSimulating ? 'Pause GPS simulation' : 'Start GPS simulation'}
        >
          {isSimulating
            ? <Pause className="text-emerald-300" style={{ width: 14, height: 14 }} strokeWidth={2.4} />
            : <Play className="text-yellow-300" style={{ width: 14, height: 14 }} strokeWidth={2.4} />}
          <span className="text-[12px] tracking-tight">Live GPS</span>
        </button>
      </div>
    </div>
  );
}

function ToggleChip({ active, onClick, color, children }) {
  const palette =
    color === 'blue'
      ? 'bg-blue-600/30 border-blue-400/70 text-blue-100'
      : 'bg-yellow-500/25 border-yellow-400/70 text-yellow-100';
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-xl border text-[11px] font-semibold transition-all ${
        active ? palette : 'bg-slate-950/60 border-slate-700 text-slate-500 hover:text-slate-200 hover:border-slate-600'
      }`}
    >
      {children}
    </button>
  );
}

function SelectedCard({ entity, onClose }) {
  const isEmp = entity.type === 'employee';
  const e = entity.data;
  return (
    <div className="nad-mapview-ui-control absolute bottom-6 left-1/2 -translate-x-1/2 z-[2000] w-[92%] sm:w-auto min-w-[340px] max-w-md bg-slate-900/95 backdrop-blur-xl border-2 border-yellow-400/90 rounded-2xl p-4 shadow-2xl shadow-blue-950/90 text-white" style={{ animation: 'nad-fade-in-up 220ms ease-out both' }}>
      <style>{`@keyframes nad-fade-in-up { from { opacity:0; transform:translate(-50%, 8px); } to { opacity:1; transform:translate(-50%, 0); } }`}</style>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          {isEmp ? (
            <div className="relative flex-shrink-0">
              <img src={e.avatar} alt={e.name} className="w-12 h-12 rounded-2xl object-cover border-2 border-blue-500 shadow-md" />
              <span className="absolute -bottom-1 -right-1 bg-blue-600 text-[9px] font-black px-1 rounded border border-slate-900">EMP</span>
            </div>
          ) : (
            <div className="w-12 h-12 flex-shrink-0 rounded-2xl bg-gradient-to-tr from-yellow-500 to-amber-300 text-white flex items-center justify-center border-2 border-blue-950 shadow-md font-bold">
              <Building2 className="w-6 h-6" />
            </div>
          )}
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h4 className="font-bold text-sm text-white truncate">
                {isEmp ? e.name : e.company}
              </h4>
              <span className={`text-[10px] font-extrabold px-2 py-0.5 rounded-full ${
                isEmp
                  ? 'bg-blue-900 text-blue-200 border border-blue-600'
                  : 'bg-yellow-400/20 text-yellow-300 border border-yellow-400/50'
              }`}>
                {isEmp ? String(e.status).replace('_', ' ').toUpperCase() : e.tier}
              </span>
            </div>
            <p className="text-xs text-slate-300 mt-0.5 truncate">
              {isEmp ? `${e.role} • ${e.department}` : `${e.industry} • ${e.city}`}
            </p>
          </div>
        </div>
        <button onClick={onClose} className="text-slate-400 hover:text-white p-1 rounded-lg hover:bg-slate-800 flex-shrink-0">
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="grid grid-cols-2 gap-2 my-3 p-2.5 bg-slate-950/60 rounded-xl border border-slate-800 text-xs">
        {isEmp ? (
          <>
            {e.battery != null && (
              <div className="flex items-center gap-1.5 text-slate-300">
                <BatteryCharging className="w-3.5 h-3.5 text-emerald-400" />
                <span>Battery: <strong>{e.battery}%</strong></span>
              </div>
            )}
            <div className={`flex items-center gap-1.5 text-slate-300 ${e.battery == null ? 'col-span-2' : ''}`}>
              <ShieldCheck className="w-3.5 h-3.5 text-blue-400" />
              <span>Latest login today: <strong>{e.lastActive || 'No records'}</strong></span>
            </div>
            <div className="col-span-2 text-[11px] text-yellow-300/90">
              Task: {e.currentTask || 'No task assigned'}
            </div>
            {e._hadCoords === false ? (
              <div className="col-span-2 text-[10.5px] text-slate-400 italic">
                (No login GPS yet — default pin shown)
              </div>
            ) : null}
          </>
        ) : (
          <>
            <div className="text-slate-300">
              Val: <strong className="text-yellow-400">{e.contractValue || '—'}</strong>
            </div>
            <div className="text-slate-300 truncate">
              Contact: <strong>{e.contactPerson || '—'}</strong>
            </div>
            <div className="flex items-center gap-1.5 text-slate-300">
              <Navigation className="w-3.5 h-3.5 text-yellow-400" />
              <span className="truncate">{e.address || 'No address'}</span>
            </div>
            <div className="flex items-center gap-1.5 text-slate-300">
              <Sparkles className="w-3.5 h-3.5 text-blue-400" />
              <span>{e.activeProjects ?? 0} active projects</span>
            </div>
          </>
        )}
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <a
          href={`mailto:${e.email || ''}`}
          className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-xl bg-slate-950/70 hover:bg-slate-800 border border-slate-700 hover:border-blue-500/50 text-xs font-medium text-slate-200 transition-all ${!e.email ? 'opacity-50 pointer-events-none' : ''}`}
        >
          <Mail className="w-3.5 h-3.5 text-blue-400" /> Email
        </a>
        <a
          href={`tel:${e.phone || ''}`}
          className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-xl bg-slate-950/70 hover:bg-slate-800 border border-slate-700 hover:border-yellow-400/50 text-xs font-medium text-slate-200 transition-all ${!e.phone ? 'opacity-50 pointer-events-none' : ''}`}
        >
          <Phone className="w-3.5 h-3.5 text-yellow-400" /> Call
        </a>
        {!isEmp && e.notes && (
          <div className="text-[11px] text-slate-400 italic flex-1 truncate">{e.notes}</div>
        )}
      </div>
    </div>
  );
}

function ModalShell({ isOpen, onClose, title, children }) {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-md"
        style={{ animation: 'nad-fade 180ms ease-out both' }}
      />
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-lg rounded-3xl border p-5 text-slate-100 shadow-2xl overflow-hidden"
        style={{
          animation: 'nad-zoom-in 220ms cubic-bezier(0.16, 1, 0.3, 1) both',
          background: 'linear-gradient(135deg, rgba(30, 64, 175, 0.65) 0%, rgba(15, 23, 42, 0.82) 55%, rgba(59, 78, 160, 0.72) 100%)',
          borderColor: 'rgba(250, 204, 21, 0.35)',
          boxShadow: '0 25px 60px -15px rgba(0,0,0,0.7), inset 0 1px 0 rgba(255,255,255,0.05), 0 0 40px rgba(59,130,246,0.15)',
          backdropFilter: 'blur(18px)',
        }}
      >
        <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full pointer-events-none" style={{ background: 'radial-gradient(circle, rgba(250,204,21,0.18), transparent 60%)' }} />
        <div className="absolute -bottom-20 -left-20 w-64 h-64 rounded-full pointer-events-none" style={{ background: 'radial-gradient(circle, rgba(244,63,94,0.10), transparent 60%)' }} />
        <div className="relative">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base font-extrabold tracking-tight" style={{ color: 'var(--nad-accent-txt, #facc15)' }}>
              {title}
            </h3>
            <button onClick={onClose} className="w-8 h-8 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 text-slate-300 hover:text-white flex items-center justify-center transition-all">
              <X className="w-4 h-4" />
            </button>
          </div>
          {children}
        </div>
        <style>{`
          @keyframes nad-fade { from { opacity: 0; } to { opacity: 1; } }
          @keyframes nad-zoom-in { from { opacity: 0; transform: scale(0.92) translateY(4px); } to { opacity: 1; transform: scale(1) translateY(0); } }
        `}</style>
      </div>
    </div>
  );
}

function AddEntityModal({ isOpen, onClose, onAddEmployee, onAddClient, initialCoords }) {
  const [mode, setMode] = useState('employee');
  const [empName, setEmpName] = useState('');
  const [empRole, setEmpRole] = useState('Field Specialist');
  const [empDept, setEmpDept] = useState('Field Engineering');
  const [empEmail, setEmpEmail] = useState('');
  const [empPhone, setEmpPhone] = useState('+1 (415) 555-0199');
  const [empStatus, setEmpStatus] = useState('active');
  const [cliCompany, setCliCompany] = useState('');
  const [cliContact, setCliContact] = useState('');
  const [cliIndustry, setCliIndustry] = useState('Technology');
  const [cliTier, setCliTier] = useState('Enterprise');
  const [cliCity, setCliCity] = useState('San Francisco');
  const [cliValue, setCliValue] = useState('$500,000 / yr');
  const [lat, setLat] = useState(initialCoords?.lat || 37.7749);
  const [lng, setLng] = useState(initialCoords?.lng || -122.4194);

  useEffect(() => {
    if (!isOpen) return;
    setLat(initialCoords?.lat || 37.7749);
    setLng(initialCoords?.lng || -122.4194);
  }, [initialCoords, isOpen]);

  const submit = (e) => {
    e.preventDefault();
    if (mode === 'employee') {
      if (!empName.trim()) return;
      const newEmp = {
        id: `emp-${Date.now()}`,
        name: empName.trim(),
        role: empRole.trim(),
        department: empDept,
        email: empEmail.trim() || `${empName.toLowerCase().replace(/\s+/g, '.')}@geopulse.io`,
        phone: empPhone.trim(),
        avatar: `https://images.unsplash.com/photo-${1500000000000 + Math.floor(Math.random() * 5000000000)}?w=150&auto=format&fit=crop&q=80`,
        lat: Number(lat),
        lng: Number(lng),
        status: empStatus,
        battery: Math.floor(Math.random() * 30) + 70,
        lastActive: 'Just now',
        currentTask: 'Assigned via Map View',
        skills: [],
      };
      onAddEmployee(newEmp);
    } else {
      if (!cliCompany.trim()) return;
      const newCli = {
        id: `cli-${Date.now()}`,
        company: cliCompany.trim(),
        contactPerson: cliContact.trim() || 'TBD Contact',
        role: 'Primary Contact',
        email: `info@${cliCompany.toLowerCase().replace(/\s+/g, '')}.com`,
        phone: '+1 (415) 555-0000',
        address: '',
        city: cliCity.trim(),
        industry: cliIndustry,
        tier: cliTier,
        lat: Number(lat),
        lng: Number(lng),
        contractValue: cliValue.trim(),
        activeProjects: 0,
        assignedEmployeeIds: [],
      };
      onAddClient(newCli);
    }
    onClose();
    setEmpName(''); setEmpEmail(''); setCliCompany(''); setCliContact('');
  };

  const inputCls = 'w-full text-xs px-3 py-2 rounded-xl bg-slate-950/70 border border-slate-700 focus:border-yellow-400/70 focus:outline-none text-slate-100 placeholder:text-slate-500';
  const labelCls = 'block text-[11px] font-bold mb-1 uppercase tracking-wider';

  return (
    <ModalShell isOpen={isOpen} onClose={onClose} title="Add Map Entity">
      <div className="flex items-center gap-1.5 p-1 mb-4 rounded-2xl bg-slate-950/60 border border-slate-700">
        <button
          onClick={() => setMode('employee')}
          className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold transition-all ${
            mode === 'employee' ? 'bg-blue-600 text-white shadow-lg shadow-blue-900/40' : 'text-slate-300 hover:text-white'
          }`}
        >
          <Users className="w-3.5 h-3.5" /> Employee
        </button>
        <button
          onClick={() => setMode('client')}
          className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold transition-all ${
            mode === 'client' ? 'bg-yellow-500 text-white shadow-lg shadow-yellow-900/40' : 'text-slate-300 hover:text-white'
          }`}
        >
          <Building2 className="w-3.5 h-3.5" /> Client
        </button>
      </div>
      <form onSubmit={submit} className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
        {mode === 'employee' ? (
          <>
            <div className="grid grid-cols-2 gap-2">
              <div className="col-span-2">
                <label className={labelCls} style={{ color: 'var(--nad-accent-txt, #facc15)' }}>Full name *</label>
                <input className={inputCls} value={empName} onChange={(e) => setEmpName(e.target.value)} placeholder="Juan Dela Cruz" />
              </div>
              <div>
                <label className={labelCls} style={{ color: 'var(--nad-accent-txt, #facc15)' }}>Role</label>
                <input className={inputCls} value={empRole} onChange={(e) => setEmpRole(e.target.value)} />
              </div>
              <div>
                <label className={labelCls} style={{ color: 'var(--nad-accent-txt, #facc15)' }}>Department</label>
                <select className={inputCls} value={empDept} onChange={(e) => setEmpDept(e.target.value)}>
                  {DEPARTMENTS.map((d) => <option key={d} value={d}>{d}</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls} style={{ color: 'var(--nad-accent-txt, #facc15)' }}>Email</label>
                <input className={inputCls} value={empEmail} onChange={(e) => setEmpEmail(e.target.value)} placeholder="auto-generated if blank" />
              </div>
              <div>
                <label className={labelCls} style={{ color: 'var(--nad-accent-txt, #facc15)' }}>Phone</label>
                <input className={inputCls} value={empPhone} onChange={(e) => setEmpPhone(e.target.value)} />
              </div>
              <div>
                <label className={labelCls} style={{ color: 'var(--nad-accent-txt, #facc15)' }}>Status</label>
                <select className={inputCls} value={empStatus} onChange={(e) => setEmpStatus(e.target.value)}>
                  {EMP_STATUSES.map((s) => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
                </select>
              </div>
            </div>
          </>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-2">
              <div className="col-span-2">
                <label className={labelCls} style={{ color: 'var(--nad-accent-txt, #facc15)' }}>Company *</label>
                <input className={inputCls} value={cliCompany} onChange={(e) => setCliCompany(e.target.value)} placeholder="Acme Inc." />
              </div>
              <div>
                <label className={labelCls} style={{ color: 'var(--nad-accent-txt, #facc15)' }}>Contact person</label>
                <input className={inputCls} value={cliContact} onChange={(e) => setCliContact(e.target.value)} />
              </div>
              <div>
                <label className={labelCls} style={{ color: 'var(--nad-accent-txt, #facc15)' }}>City</label>
                <input className={inputCls} value={cliCity} onChange={(e) => setCliCity(e.target.value)} />
              </div>
              <div>
                <label className={labelCls} style={{ color: 'var(--nad-accent-txt, #facc15)' }}>Industry</label>
                <select className={inputCls} value={cliIndustry} onChange={(e) => setCliIndustry(e.target.value)}>
                  {INDUSTRIES.map((i) => <option key={i} value={i}>{i}</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls} style={{ color: 'var(--nad-accent-txt, #facc15)' }}>Tier</label>
                <select className={inputCls} value={cliTier} onChange={(e) => setCliTier(e.target.value)}>
                  {CLIENT_TIERS.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div className="col-span-2">
                <label className={labelCls} style={{ color: 'var(--nad-accent-txt, #facc15)' }}>Contract value</label>
                <input className={inputCls} value={cliValue} onChange={(e) => setCliValue(e.target.value)} />
              </div>
            </div>
          </>
        )}
        <div className="grid grid-cols-2 gap-2 pt-2 border-t border-white/10">
          <div>
            <label className={labelCls} style={{ color: 'var(--nad-accent-txt, #facc15)' }}>Latitude</label>
            <input type="number" step="any" className={inputCls} value={lat} onChange={(e) => setLat(e.target.value)} />
          </div>
          <div>
            <label className={labelCls} style={{ color: 'var(--nad-accent-txt, #facc15)' }}>Longitude</label>
            <input type="number" step="any" className={inputCls} value={lng} onChange={(e) => setLng(e.target.value)} />
          </div>
          <div className="col-span-2 text-[10px] text-slate-400 italic">
            Tip: click on the map first before opening Add to auto-fill coordinates.
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-xl text-xs font-semibold bg-white/5 hover:bg-white/10 border border-white/10 text-slate-200 transition-all">
            Cancel
          </button>
          <button
            type="submit"
            className="px-4 py-2 rounded-xl text-xs font-bold shadow-lg transition-all"
            style={{
              background: 'linear-gradient(135deg, #2563eb, #facc15)',
              color: '#020617',
              boxShadow: '0 10px 25px -10px rgba(250,204,21,0.6)',
            }}
          >
            <Plus className="w-3.5 h-3.5 inline mr-1" /> Add {mode === 'employee' ? 'Employee' : 'Client'}
          </button>
        </div>
      </form>
    </ModalShell>
  );
}

function RadiusModal({ isOpen, onClose, clients, onApply, onClear, currentClient, currentKm }) {
  const [clientId, setClientId] = useState(currentClient?.id || clients[0]?.id || '');
  const [km, setKm] = useState(currentKm || 15);

  useEffect(() => {
    if (isOpen) {
      setClientId(currentClient?.id || clients[0]?.id || '');
      setKm(currentKm || 15);
    }
  }, [isOpen, currentClient, currentKm, clients]);

  const clientsWithAssigned = clients.slice().sort((a, b) => b.assignedEmployeeIds.length - a.assignedEmployeeIds.length);
  const selected = clients.find((c) => c.id === clientId);
  const nearby = useMemo(() => {
    if (!selected) return [];
    const R = 6371;
    const toRad = (d) => (d * Math.PI) / 180;
    return (globalThis.__radius_employees_cache || []).filter(() => false);
  }, [selected]);

  const inputCls = 'w-full text-xs px-3 py-2 rounded-xl bg-slate-950/70 border border-slate-700 focus:border-yellow-400/70 focus:outline-none text-slate-100 placeholder:text-slate-500';
  const labelCls = 'block text-[11px] font-bold mb-1 uppercase tracking-wider';

  return (
    <ModalShell isOpen={isOpen} onClose={onClose} title="Radius Proximity Tool">
      <div className="space-y-3">
        <div>
          <label className={labelCls} style={{ color: 'var(--nad-accent-txt, #facc15)' }}>Anchor client site</label>
          <select className={inputCls} value={clientId} onChange={(e) => setClientId(e.target.value)}>
            {clientsWithAssigned.map((c) => (
              <option key={c.id} value={c.id}>
                {c.company} ({c.city} · {c.tier})
              </option>
            ))}
          </select>
        </div>
        <div>
          <div className="flex items-center justify-between mb-1">
            <label className={labelCls} style={{ color: 'var(--nad-accent-txt, #facc15)' }}>Radius</label>
            <span className="text-xs font-extrabold text-yellow-300">{km} km</span>
          </div>
          <input
            type="range"
            min={1}
            max={100}
            value={km}
            onChange={(e) => setKm(Number(e.target.value))}
            className="w-full accent-yellow-400"
          />
          <div className="flex justify-between text-[10px] text-slate-500 px-1 mt-1">
            <span>1 km</span><span>25 km</span><span>50 km</span><span>100 km</span>
          </div>
        </div>
        {selected && (
          <div className="p-2.5 rounded-xl bg-slate-950/60 border border-slate-800 text-xs space-y-1">
            <div className="flex items-center gap-2">
              <span className="inline-flex w-2 h-2 rounded-full bg-yellow-400" />
              <strong className="text-white truncate">{selected.company}</strong>
              <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-md bg-yellow-500/20 text-yellow-200 border border-yellow-400/40">{selected.tier}</span>
            </div>
            <div className="text-slate-400">
              {selected.address || selected.city} · assigned staff: {selected.assignedEmployeeIds.length}
            </div>
            <div className="text-[11px] text-slate-400">
              Coordinates: {selected.lat.toFixed(4)}, {selected.lng.toFixed(4)}
            </div>
          </div>
        )}
        <div className="flex items-center justify-between gap-2 pt-2">
          <button
            type="button"
            onClick={() => { onClear(); onClose(); }}
            className="px-3 py-2 rounded-xl text-xs font-semibold bg-rose-500/15 hover:bg-rose-500/25 border border-rose-500/30 text-rose-300 hover:text-rose-200 transition-all"
          >
            Clear Radius
          </button>
          <div className="flex items-center gap-2">
            <button type="button" onClick={onClose} className="px-3 py-2 rounded-xl text-xs font-semibold bg-white/5 hover:bg-white/10 border border-white/10 text-slate-200 transition-all">
              Cancel
            </button>
            <button
              onClick={() => {
                if (selected) onApply(selected, km);
                onClose();
              }}
              className="px-4 py-2 rounded-xl text-xs font-bold shadow-lg transition-all"
              style={{
                background: 'linear-gradient(135deg, #3b82f6, #facc15)',
                color: '#020617',
                boxShadow: '0 10px 25px -10px rgba(59,130,246,0.6)',
              }}
            >
              <CircleDot className="w-3.5 h-3.5 inline mr-1" /> Apply Radius
            </button>
          </div>
        </div>
      </div>
    </ModalShell>
  );
}

function HelpModal({ isOpen, onClose }) {
  const items = [
    ['Click marker', 'Select an employee or client to view details'],
    ['Filters (top-right)', 'Toggle visibility, filter by status/tier/dept/industry'],
    ['GPS Sim', 'Animate movement for active/in-transit employees'],
    ['Add', 'Drop a new employee or client anywhere on the map'],
    ['Radius', 'Draw a proximity circle around a client site'],
    ['Map Style', 'Switch between Voyager, OSM, Positron, and Dark tiles'],
    ['Zoom controls', 'Bottom-right: in/out, reset center, fullscreen'],
  ];
  return (
    <ModalShell isOpen={isOpen} onClose={onClose} title="Map View · Quick Help">
      <div className="space-y-2 max-h-[60vh] overflow-y-auto pr-1">
        {items.map(([k, v]) => (
          <div key={k} className="flex items-start gap-3 p-2.5 rounded-xl bg-slate-950/50 border border-slate-800">
            <div className="flex-shrink-0 w-7 h-7 rounded-lg bg-gradient-to-br from-blue-600 to-yellow-500 flex items-center justify-center text-[11px] font-black text-white shadow-md">
              ?
            </div>
            <div>
              <div className="text-xs font-bold text-white">{k}</div>
              <div className="text-[11px] text-slate-400 mt-0.5">{v}</div>
            </div>
          </div>
        ))}
        <div className="mt-3 p-3 rounded-2xl border border-yellow-400/40 bg-yellow-400/5">
          <div className="text-[11px] font-bold text-yellow-300 mb-1">Tip</div>
          <div className="text-[11px] text-slate-300 leading-relaxed">
            Click anywhere on the empty map, then open the <strong>Add</strong> menu to pre-fill the pin coordinates.
            Combine with the <strong>Radius</strong> tool to see coverage gaps!
          </div>
        </div>
      </div>
    </ModalShell>
  );
}
